import { baseApiClient as api } from "../baseService";

export const addTagTypes = [
  "firms",
  "blob-sink-config",
  "case-manager",
  "cms-feature-config",
  "services",
  "workflows",
  "sms-templates",
  "firm-task-templates",
  "documents",
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      firmsList: build.query<FirmsListApiResponse, FirmsListApiArg>({
        query: () => ({ url: `/api/firms/` }),
        providesTags: ["firms"],
      }),
      firmsCreate: build.mutation<FirmsCreateApiResponse, FirmsCreateApiArg>({
        query: (queryArg) => ({
          url: `/api/firms/`,
          method: "POST",
          body: queryArg.firm,
        }),
        invalidatesTags: ["firms"],
      }),
      firmsBlobSinkConfigsList: build.query<
        FirmsBlobSinkConfigsListApiResponse,
        FirmsBlobSinkConfigsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/`,
        }),
        providesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsCreate: build.mutation<
        FirmsBlobSinkConfigsCreateApiResponse,
        FirmsBlobSinkConfigsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/`,
          method: "POST",
          body: queryArg.createBlobSinkConfigSchema,
        }),
        invalidatesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsRetrieve: build.query<
        FirmsBlobSinkConfigsRetrieveApiResponse,
        FirmsBlobSinkConfigsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/`,
        }),
        providesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsPartialUpdate: build.mutation<
        FirmsBlobSinkConfigsPartialUpdateApiResponse,
        FirmsBlobSinkConfigsPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateBlobSinkConfigSchema,
        }),
        invalidatesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsDestroy: build.mutation<
        FirmsBlobSinkConfigsDestroyApiResponse,
        FirmsBlobSinkConfigsDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsPromptPreviewRuns: build.query<
        FirmsBlobSinkConfigsPromptPreviewRunsApiResponse,
        FirmsBlobSinkConfigsPromptPreviewRunsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/prompt-preview-runs/`,
        }),
        providesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsPromptPreviewRuns2: build.mutation<
        FirmsBlobSinkConfigsPromptPreviewRuns2ApiResponse,
        FirmsBlobSinkConfigsPromptPreviewRuns2ApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/prompt-preview-runs/`,
          method: "POST",
          body: queryArg.createBlobSinkPromptPreviewRunSchema,
        }),
        invalidatesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsPromptPreviewRunsRetrieve: build.query<
        FirmsBlobSinkConfigsPromptPreviewRunsRetrieveApiResponse,
        FirmsBlobSinkConfigsPromptPreviewRunsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/prompt-preview-runs/${queryArg.runId}/`,
        }),
        providesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsPromptPreviewRunsApply: build.mutation<
        FirmsBlobSinkConfigsPromptPreviewRunsApplyApiResponse,
        FirmsBlobSinkConfigsPromptPreviewRunsApplyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/prompt-preview-runs/${queryArg.runId}/apply/`,
          method: "POST",
        }),
        invalidatesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsPromptPreviewRunsLatest: build.query<
        FirmsBlobSinkConfigsPromptPreviewRunsLatestApiResponse,
        FirmsBlobSinkConfigsPromptPreviewRunsLatestApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.id}/prompt-preview-runs/latest/`,
        }),
        providesTags: ["blob-sink-config"],
      }),
      firmsBlobSinkConfigsGuessConvention: build.query<
        FirmsBlobSinkConfigsGuessConventionApiResponse,
        FirmsBlobSinkConfigsGuessConventionApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/guess_convention/`,
          params: {
            target_system: queryArg.targetSystem,
          },
        }),
        providesTags: ["blob-sink-config"],
      }),
      firmCaseManagersList: build.query<
        FirmCaseManagersListApiResponse,
        FirmCaseManagersListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/case-managers/`,
        }),
        providesTags: ["case-manager"],
      }),
      firmsCmsFeatureConfigsList: build.query<
        FirmsCmsFeatureConfigsListApiResponse,
        FirmsCmsFeatureConfigsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/`,
          params: {
            cms_type: queryArg.cmsType,
          },
        }),
        providesTags: ["cms-feature-config"],
      }),
      firmsCmsFeatureConfigsCreate: build.mutation<
        FirmsCmsFeatureConfigsCreateApiResponse,
        FirmsCmsFeatureConfigsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/`,
          method: "POST",
          body: queryArg.createCmsFeatureConfigSchema,
        }),
        invalidatesTags: ["cms-feature-config"],
      }),
      firmsCmsFeatureConfigsRetrieve: build.query<
        FirmsCmsFeatureConfigsRetrieveApiResponse,
        FirmsCmsFeatureConfigsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/`,
        }),
        providesTags: ["cms-feature-config"],
      }),
      firmsCmsFeatureConfigsPartialUpdate: build.mutation<
        FirmsCmsFeatureConfigsPartialUpdateApiResponse,
        FirmsCmsFeatureConfigsPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateCmsFeatureConfigSchema,
        }),
        invalidatesTags: ["cms-feature-config"],
      }),
      firmsCmsFeatureConfigsDestroy: build.mutation<
        FirmsCmsFeatureConfigsDestroyApiResponse,
        FirmsCmsFeatureConfigsDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["cms-feature-config"],
      }),
      firmsCmsFeatureConfigsDetails: build.query<
        FirmsCmsFeatureConfigsDetailsApiResponse,
        FirmsCmsFeatureConfigsDetailsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/details/`,
          params: {
            run_limit: queryArg.runLimit,
          },
        }),
        providesTags: ["cms-feature-config"],
      }),
      firmsCmsFeatureConfigsTrigger: build.mutation<
        FirmsCmsFeatureConfigsTriggerApiResponse,
        FirmsCmsFeatureConfigsTriggerApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/${queryArg.id}/trigger/`,
          method: "POST",
        }),
        invalidatesTags: ["cms-feature-config"],
      }),
      firmsCmsFeatureConfigsSupportedFeatures: build.query<
        FirmsCmsFeatureConfigsSupportedFeaturesApiResponse,
        FirmsCmsFeatureConfigsSupportedFeaturesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/cms-feature-configs/supported-features/`,
        }),
        providesTags: ["cms-feature-config"],
      }),
      externalTaskRuleList: build.query<
        ExternalTaskRuleListApiResponse,
        ExternalTaskRuleListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/`,
          params: {
            status: queryArg.status,
          },
        }),
        providesTags: ["firms"],
      }),
      externalTaskRuleCreate: build.mutation<
        ExternalTaskRuleCreateApiResponse,
        ExternalTaskRuleCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/`,
          method: "POST",
          body: queryArg.createExternalTaskRuleSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      externalTaskRuleRetrieve: build.query<
        ExternalTaskRuleRetrieveApiResponse,
        ExternalTaskRuleRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/${queryArg.id}/`,
        }),
        providesTags: ["firms"],
      }),
      externalTaskRulePartialUpdate: build.mutation<
        ExternalTaskRulePartialUpdateApiResponse,
        ExternalTaskRulePartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateExternalTaskRuleSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      externalTaskRuleDestroy: build.mutation<
        ExternalTaskRuleDestroyApiResponse,
        ExternalTaskRuleDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["firms"],
      }),
      externalTaskRuleUpdateConfig: build.mutation<
        ExternalTaskRuleUpdateConfigApiResponse,
        ExternalTaskRuleUpdateConfigApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/${queryArg.id}/update-config/`,
          method: "POST",
          body: queryArg.updateExternalTaskRuleConfigSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      externalTaskRuleVersions: build.query<
        ExternalTaskRuleVersionsApiResponse,
        ExternalTaskRuleVersionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/${queryArg.id}/versions/`,
        }),
        providesTags: ["firms"],
      }),
      externalTaskRuleStartBackfill: build.mutation<
        ExternalTaskRuleStartBackfillApiResponse,
        ExternalTaskRuleStartBackfillApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/backfill/`,
          method: "POST",
        }),
        invalidatesTags: ["firms"],
      }),
      externalTaskRuleGetBackfillProgress: build.query<
        ExternalTaskRuleGetBackfillProgressApiResponse,
        ExternalTaskRuleGetBackfillProgressApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/backfill/${queryArg.runId}/`,
        }),
        providesTags: ["firms"],
      }),
      externalTaskRuleCancelBackfill: build.mutation<
        ExternalTaskRuleCancelBackfillApiResponse,
        ExternalTaskRuleCancelBackfillApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/backfill/${queryArg.runId}/cancel/`,
          method: "POST",
        }),
        invalidatesTags: ["firms"],
      }),
      externalTaskRuleListBackfills: build.query<
        ExternalTaskRuleListBackfillsApiResponse,
        ExternalTaskRuleListBackfillsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/backfills/`,
        }),
        providesTags: ["firms"],
      }),
      externalTaskRuleDistinctNames: build.query<
        ExternalTaskRuleDistinctNamesApiResponse,
        ExternalTaskRuleDistinctNamesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/distinct-task-names/`,
          params: {
            assignee_names: queryArg.assigneeNames,
            description_contains: queryArg.descriptionContains,
            min_count: queryArg.minCount,
            name_contains: queryArg.nameContains,
            name_not_contains: queryArg.nameNotContains,
            statuses: queryArg.statuses,
          },
        }),
        providesTags: ["firms"],
      }),
      externalTaskRuleLiveTest: build.mutation<
        ExternalTaskRuleLiveTestApiResponse,
        ExternalTaskRuleLiveTestApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/live-test/`,
          method: "POST",
          body: queryArg.liveTestRequestSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      externalTaskRulePreviewExternalFilter: build.mutation<
        ExternalTaskRulePreviewExternalFilterApiResponse,
        ExternalTaskRulePreviewExternalFilterApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/preview-external-filter/`,
          method: "POST",
          body: queryArg.previewExternalFilterRequestSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      externalTaskRulePreviewInternalFilter: build.mutation<
        ExternalTaskRulePreviewInternalFilterApiResponse,
        ExternalTaskRulePreviewInternalFilterApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/external-task-rules/preview-internal-filter/`,
          method: "POST",
          body: queryArg.previewInternalFilterRequestSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      filevineOrganizationLinkGet: build.query<
        FilevineOrganizationLinkGetApiResponse,
        FilevineOrganizationLinkGetApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-organization/get_link/`,
        }),
        providesTags: ["firms"],
      }),
      filevineOrganizationLinkCreate: build.mutation<
        FilevineOrganizationLinkCreateApiResponse,
        FilevineOrganizationLinkCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-organization/link/`,
          method: "POST",
          body: queryArg.linkFirmToOrganizationSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      filevineOrganizationsList: build.query<
        FilevineOrganizationsListApiResponse,
        FilevineOrganizationsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-organization/organizations/`,
        }),
        providesTags: ["firms"],
      }),
      filevineOrganizationUnlink: build.mutation<
        FilevineOrganizationUnlinkApiResponse,
        FilevineOrganizationUnlinkApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-organization/unlink/`,
          method: "POST",
        }),
        invalidatesTags: ["firms"],
      }),
      filevineProjectConfigsList: build.query<
        FilevineProjectConfigsListApiResponse,
        FilevineProjectConfigsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-project-configs/`,
        }),
        providesTags: ["firms"],
      }),
      filevineProjectConfigSyncTrigger: build.mutation<
        FilevineProjectConfigSyncTriggerApiResponse,
        FilevineProjectConfigSyncTriggerApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-project-configs/sync/`,
          method: "POST",
          body: queryArg.triggerProjectConfigSyncSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      filevineSectionConfigsList: build.query<
        FilevineSectionConfigsListApiResponse,
        FilevineSectionConfigsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-sections/`,
        }),
        providesTags: ["firms"],
      }),
      filevineSectionConfigRetrieve: build.query<
        FilevineSectionConfigRetrieveApiResponse,
        FilevineSectionConfigRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-sections/${queryArg.id}/`,
        }),
        providesTags: ["firms"],
      }),
      filevineSectionConfigUpdateType: build.mutation<
        FilevineSectionConfigUpdateTypeApiResponse,
        FilevineSectionConfigUpdateTypeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine-sections/${queryArg.id}/type/`,
          method: "PATCH",
          body: queryArg.patchedUpdateSectionTypeSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      filevineContactRetrieve: build.query<
        FilevineContactRetrieveApiResponse,
        FilevineContactRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine/contacts/${queryArg.id}/`,
        }),
        providesTags: ["firms"],
      }),
      filevineContactByFilevineId: build.query<
        FilevineContactByFilevineIdApiResponse,
        FilevineContactByFilevineIdApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine/contacts/by-filevine-id/${queryArg.filevineId}/`,
        }),
        providesTags: ["firms"],
      }),
      filevineContactSearch: build.query<
        FilevineContactSearchApiResponse,
        FilevineContactSearchApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/filevine/contacts/search/`,
          params: {
            field_types: queryArg.fieldTypes,
            include_archived: queryArg.includeArchived,
            limit: queryArg.limit,
            q: queryArg.q,
          },
        }),
        providesTags: ["firms"],
      }),
      printableFilevineSectionsList: build.query<
        PrintableFilevineSectionsListApiResponse,
        PrintableFilevineSectionsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/printable-filevine-sections/`,
        }),
        providesTags: ["firms"],
      }),
      printableFilevineSectionsCreate: build.mutation<
        PrintableFilevineSectionsCreateApiResponse,
        PrintableFilevineSectionsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/printable-filevine-sections/`,
          method: "POST",
          body: queryArg.createPrintableFilevineSectionSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      printableFilevineSectionsRetrieve: build.query<
        PrintableFilevineSectionsRetrieveApiResponse,
        PrintableFilevineSectionsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/printable-filevine-sections/${queryArg.id}/`,
        }),
        providesTags: ["firms"],
      }),
      printableFilevineSectionsUpdate: build.mutation<
        PrintableFilevineSectionsUpdateApiResponse,
        PrintableFilevineSectionsUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/printable-filevine-sections/${queryArg.id}/`,
          method: "PUT",
          body: queryArg.updatePrintableFilevineSectionSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      printableFilevineSectionsPartialUpdate: build.mutation<
        PrintableFilevineSectionsPartialUpdateApiResponse,
        PrintableFilevineSectionsPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/printable-filevine-sections/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdatePrintableFilevineSectionSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      printableFilevineSectionsDestroy: build.mutation<
        PrintableFilevineSectionsDestroyApiResponse,
        PrintableFilevineSectionsDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/printable-filevine-sections/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["firms"],
      }),
      getFirmServices: build.query<
        GetFirmServicesApiResponse,
        GetFirmServicesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/services/`,
        }),
        providesTags: ["services"],
      }),
      createFirmService: build.mutation<
        CreateFirmServiceApiResponse,
        CreateFirmServiceApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/services/`,
          method: "POST",
          body: queryArg.createFirmServiceSchema,
        }),
        invalidatesTags: ["services"],
      }),
      bulkUpdateFirmServiceWorkflows: build.mutation<
        BulkUpdateFirmServiceWorkflowsApiResponse,
        BulkUpdateFirmServiceWorkflowsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/services/${queryArg.firmServiceId}/workflows/bulk/`,
          method: "POST",
          body: queryArg.updateFirmServiceWorkflowsRequest,
        }),
        invalidatesTags: ["services", "workflows"],
      }),
      updateFirmService: build.mutation<
        UpdateFirmServiceApiResponse,
        UpdateFirmServiceApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/services/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateFirmServiceSchema,
        }),
        invalidatesTags: ["services"],
      }),
      activateFirmServices: build.mutation<
        ActivateFirmServicesApiResponse,
        ActivateFirmServicesApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/services/activate/`,
          method: "POST",
        }),
        invalidatesTags: ["services"],
      }),
      getFirmAutoCcEmails: build.query<
        GetFirmAutoCcEmailsApiResponse,
        GetFirmAutoCcEmailsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/services/auto-cc-emails/`,
        }),
        providesTags: ["services"],
      }),
      getFirmServicesStatus: build.query<
        GetFirmServicesStatusApiResponse,
        GetFirmServicesStatusApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/services/status/`,
        }),
        providesTags: ["services"],
      }),
      executeFirmServicesSync: build.mutation<
        ExecuteFirmServicesSyncApiResponse,
        ExecuteFirmServicesSyncApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/services/sync/`,
          method: "POST",
        }),
        invalidatesTags: ["services"],
      }),
      previewFirmServicesSync: build.query<
        PreviewFirmServicesSyncApiResponse,
        PreviewFirmServicesSyncApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/services/sync/preview/`,
        }),
        providesTags: ["services"],
      }),
      firmSmsTemplatesList: build.query<
        FirmSmsTemplatesListApiResponse,
        FirmSmsTemplatesListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/sms-templates/`,
          params: {
            active_only: queryArg.activeOnly,
            category: queryArg.category,
          },
        }),
        providesTags: ["sms-templates"],
      }),
      firmSmsTemplatesCreate: build.mutation<
        FirmSmsTemplatesCreateApiResponse,
        FirmSmsTemplatesCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/sms-templates/`,
          method: "POST",
          body: queryArg.createSmsTemplateRequest,
        }),
        invalidatesTags: ["sms-templates"],
      }),
      firmSmsTemplatesRetrieve: build.query<
        FirmSmsTemplatesRetrieveApiResponse,
        FirmSmsTemplatesRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/sms-templates/${queryArg.id}/`,
        }),
        providesTags: ["sms-templates"],
      }),
      firmSmsTemplatesPartialUpdate: build.mutation<
        FirmSmsTemplatesPartialUpdateApiResponse,
        FirmSmsTemplatesPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/sms-templates/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateSmsTemplateRequest,
        }),
        invalidatesTags: ["sms-templates"],
      }),
      firmSmsTemplatesDestroy: build.mutation<
        FirmSmsTemplatesDestroyApiResponse,
        FirmSmsTemplatesDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/sms-templates/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["sms-templates"],
      }),
      firmTaskTemplatesList: build.query<
        FirmTaskTemplatesListApiResponse,
        FirmTaskTemplatesListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/task-templates/`,
        }),
        providesTags: ["firm-task-templates"],
      }),
      firmTaskTemplatesCreate: build.mutation<
        FirmTaskTemplatesCreateApiResponse,
        FirmTaskTemplatesCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/task-templates/`,
          method: "POST",
          body: queryArg.createFirmTaskTemplateSchema,
        }),
        invalidatesTags: ["firm-task-templates"],
      }),
      firmTaskTemplatesRetrieve: build.query<
        FirmTaskTemplatesRetrieveApiResponse,
        FirmTaskTemplatesRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/task-templates/${queryArg.id}/`,
        }),
        providesTags: ["firm-task-templates"],
      }),
      firmTaskTemplatesPartialUpdate: build.mutation<
        FirmTaskTemplatesPartialUpdateApiResponse,
        FirmTaskTemplatesPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/task-templates/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateFirmTaskTemplateSchema,
        }),
        invalidatesTags: ["firm-task-templates"],
      }),
      firmTaskTemplatesApply: build.mutation<
        FirmTaskTemplatesApplyApiResponse,
        FirmTaskTemplatesApplyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/task-templates/${queryArg.id}/apply/`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["firm-task-templates"],
      }),
      firmTaskTemplatesApplyPreview: build.mutation<
        FirmTaskTemplatesApplyPreviewApiResponse,
        FirmTaskTemplatesApplyPreviewApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/task-templates/${queryArg.id}/apply/preview/`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["firm-task-templates"],
      }),
      firmTaskTemplatesOptions: build.query<
        FirmTaskTemplatesOptionsApiResponse,
        FirmTaskTemplatesOptionsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/task-templates/options/`,
        }),
        providesTags: ["firm-task-templates"],
      }),
      documentTemplatesList: build.query<
        DocumentTemplatesListApiResponse,
        DocumentTemplatesListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/templates/`,
        }),
        providesTags: ["documents"],
      }),
      documentTemplatesCreate: build.mutation<
        DocumentTemplatesCreateApiResponse,
        DocumentTemplatesCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/templates/`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["documents"],
      }),
      documentTemplateDownload: build.query<
        DocumentTemplateDownloadApiResponse,
        DocumentTemplateDownloadApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/templates/${queryArg.templateId}/download/`,
        }),
        providesTags: ["documents"],
      }),
      documentTemplateEditorConfig: build.query<
        DocumentTemplateEditorConfigApiResponse,
        DocumentTemplateEditorConfigApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/templates/${queryArg.templateId}/editor-config/`,
        }),
        providesTags: ["documents"],
      }),
      firmDialpadNumbersList: build.query<
        FirmDialpadNumbersListApiResponse,
        FirmDialpadNumbersListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmPk}/dialpad-numbers/`,
        }),
        providesTags: ["firms"],
      }),
      listFirmRoleAssignments: build.query<
        ListFirmRoleAssignmentsApiResponse,
        ListFirmRoleAssignmentsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmPk}/role-assignments/`,
        }),
        providesTags: ["firms"],
      }),
      createFirmRoleAssignment: build.mutation<
        CreateFirmRoleAssignmentApiResponse,
        CreateFirmRoleAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmPk}/role-assignments/`,
          method: "POST",
          body: queryArg.createFirmRoleAssignmentSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      getFirmRoleAssignment: build.query<
        GetFirmRoleAssignmentApiResponse,
        GetFirmRoleAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmPk}/role-assignments/${queryArg.id}/`,
        }),
        providesTags: ["firms"],
      }),
      updateFirmRoleAssignment: build.mutation<
        UpdateFirmRoleAssignmentApiResponse,
        UpdateFirmRoleAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmPk}/role-assignments/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateFirmRoleAssignmentSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      deleteFirmRoleAssignment: build.mutation<
        DeleteFirmRoleAssignmentApiResponse,
        DeleteFirmRoleAssignmentApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmPk}/role-assignments/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["firms"],
      }),
      firmsRetrieve: build.query<FirmsRetrieveApiResponse, FirmsRetrieveApiArg>(
        {
          query: (queryArg) => ({ url: `/api/firms/${queryArg.id}/` }),
          providesTags: ["firms"],
        },
      ),
      firmsUpdate: build.mutation<FirmsUpdateApiResponse, FirmsUpdateApiArg>({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/`,
          method: "PUT",
          body: queryArg.firm,
        }),
        invalidatesTags: ["firms"],
      }),
      firmsPartialUpdate: build.mutation<
        FirmsPartialUpdateApiResponse,
        FirmsPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedFirm,
        }),
        invalidatesTags: ["firms"],
      }),
      getFirmAddress: build.query<
        GetFirmAddressApiResponse,
        GetFirmAddressApiArg
      >({
        query: (queryArg) => ({ url: `/api/firms/${queryArg.id}/address/` }),
        providesTags: ["firms"],
      }),
      setFirmAddress2: build.mutation<
        SetFirmAddress2ApiResponse,
        SetFirmAddress2ApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/address/`,
          method: "PUT",
          body: queryArg.createAddressSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      setFirmAddress: build.mutation<
        SetFirmAddressApiResponse,
        SetFirmAddressApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/address/`,
          method: "PATCH",
          body: queryArg.patchedCreateAddressSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      setClientPortalLogo: build.mutation<
        SetClientPortalLogoApiResponse,
        SetClientPortalLogoApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/set_client_portal_logo/`,
          method: "POST",
          body: queryArg.setClientPortalLogoSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      setFirmFooter: build.mutation<
        SetFirmFooterApiResponse,
        SetFirmFooterApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/set_firm_footer/`,
          method: "POST",
          body: queryArg.setFirmFooterSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      setFirmLogo: build.mutation<SetFirmLogoApiResponse, SetFirmLogoApiArg>({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/set_firm_logo/`,
          method: "POST",
          body: queryArg.setFirmLogoSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      setFirmWatermark: build.mutation<
        SetFirmWatermarkApiResponse,
        SetFirmWatermarkApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/set_firm_watermark/`,
          method: "POST",
          body: queryArg.setFirmWatermarkSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      setHipaaAttorneyRelationshipForm: build.mutation<
        SetHipaaAttorneyRelationshipFormApiResponse,
        SetHipaaAttorneyRelationshipFormApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/set_hipaa_attorney_relationship_form/`,
          method: "POST",
          body: queryArg.setHipaaAttorneyRelationshipFormSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      setHipaaReproductiveRightsAttestation: build.mutation<
        SetHipaaReproductiveRightsAttestationApiResponse,
        SetHipaaReproductiveRightsAttestationApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/set_hipaa_reproductive_rights_attestation/`,
          method: "POST",
          body: queryArg.setHipaaReproductiveRightsAttestationSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      setLetterhead: build.mutation<
        SetLetterheadApiResponse,
        SetLetterheadApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.id}/set_letterhead/`,
          method: "POST",
          body: queryArg.setFirmLetterheadSchema,
        }),
        invalidatesTags: ["firms"],
      }),
      getServiceCatalogue: build.query<
        GetServiceCatalogueApiResponse,
        GetServiceCatalogueApiArg
      >({
        query: () => ({ url: `/api/services/catalogue/` }),
        providesTags: ["services"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type FirmsListApiResponse = /** status 200  */ FirmLiteRead[];
export type FirmsListApiArg = void;
export type FirmsCreateApiResponse = /** status 201  */ FirmRead;
export type FirmsCreateApiArg = {
  firm: FirmWrite;
};
export type FirmsBlobSinkConfigsListApiResponse =
  /** status 200  */ BlobSinkConfigSchema[];
export type FirmsBlobSinkConfigsListApiArg = {
  firmId: string;
};
export type FirmsBlobSinkConfigsCreateApiResponse =
  /** status 201  */ BlobSinkConfigSchema;
export type FirmsBlobSinkConfigsCreateApiArg = {
  firmId: string;
  createBlobSinkConfigSchema: CreateBlobSinkConfigSchema;
};
export type FirmsBlobSinkConfigsRetrieveApiResponse =
  /** status 200  */ BlobSinkConfigSchema;
export type FirmsBlobSinkConfigsRetrieveApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the blob sink configuration */
  pk: string;
};
export type FirmsBlobSinkConfigsPartialUpdateApiResponse =
  /** status 200  */ BlobSinkConfigSchema;
export type FirmsBlobSinkConfigsPartialUpdateApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the blob sink configuration */
  pk: string;
  patchedUpdateBlobSinkConfigSchema: UpdateBlobSinkConfigSchema;
};
export type FirmsBlobSinkConfigsDestroyApiResponse = unknown;
export type FirmsBlobSinkConfigsDestroyApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the blob sink configuration */
  pk: string;
};
export type FirmsBlobSinkConfigsPromptPreviewRunsApiResponse =
  /** status 200  */
    | BlobSinkPromptPreviewRunSchema
    | /** status 202  */ BlobSinkPromptPreviewRunSchema;
export type FirmsBlobSinkConfigsPromptPreviewRunsApiArg = {
  firmId: string;
  id: string;
};
export type FirmsBlobSinkConfigsPromptPreviewRuns2ApiResponse =
  /** status 200  */
    | BlobSinkPromptPreviewRunSchema
    | /** status 202  */ BlobSinkPromptPreviewRunSchema;
export type FirmsBlobSinkConfigsPromptPreviewRuns2ApiArg = {
  firmId: string;
  id: string;
  createBlobSinkPromptPreviewRunSchema: CreateBlobSinkPromptPreviewRunSchema;
};
export type FirmsBlobSinkConfigsPromptPreviewRunsRetrieveApiResponse =
  /** status 200  */ BlobSinkPromptPreviewRunDetailsSchema;
export type FirmsBlobSinkConfigsPromptPreviewRunsRetrieveApiArg = {
  firmId: string;
  id: string;
  runId: string;
};
export type FirmsBlobSinkConfigsPromptPreviewRunsApplyApiResponse =
  /** status 200  */ BlobSinkConfigSchema;
export type FirmsBlobSinkConfigsPromptPreviewRunsApplyApiArg = {
  firmId: string;
  id: string;
  runId: string;
};
export type FirmsBlobSinkConfigsPromptPreviewRunsLatestApiResponse =
  /** status 200  */ BlobSinkPromptPreviewRunDetailsSchema;
export type FirmsBlobSinkConfigsPromptPreviewRunsLatestApiArg = {
  firmId: string;
  id: string;
};
export type FirmsBlobSinkConfigsGuessConventionApiResponse =
  /** status 200  */ GuessConventionResponseSchema;
export type FirmsBlobSinkConfigsGuessConventionApiArg = {
  firmId: string;
  /** The target system to guess the convention for (e.g., FILEVINE) */
  targetSystem:
    | "CASEPEER"
    | "CLIO"
    | "DROPBOX"
    | "FILEVINE"
    | "GOOGLE_DRIVE"
    | "LITIFY"
    | "MYCASE"
    | "NEOS"
    | "SMARTADVOCATE";
};
export type FirmCaseManagersListApiResponse =
  /** status 200  */ FirmCaseManagerSchema[];
export type FirmCaseManagersListApiArg = {
  /** Firm ID */
  firmId: string;
};
export type FirmsCmsFeatureConfigsListApiResponse =
  /** status 200  */ CmsFeatureConfigSchema[];
export type FirmsCmsFeatureConfigsListApiArg = {
  /** Filter by CMS type (filevine, clio, neos, etc.) */
  cmsType?:
    | "casepeer"
    | "clio"
    | "dropbox"
    | "filevine"
    | "google_drive"
    | "litify"
    | "mycase"
    | "neos"
    | "smartadvocate";
  firmId: string;
};
export type FirmsCmsFeatureConfigsCreateApiResponse =
  /** status 201  */ CmsFeatureConfigSchema;
export type FirmsCmsFeatureConfigsCreateApiArg = {
  firmId: string;
  createCmsFeatureConfigSchema: CreateCmsFeatureConfigSchema;
};
export type FirmsCmsFeatureConfigsRetrieveApiResponse =
  /** status 200  */ CmsFeatureConfigSchema;
export type FirmsCmsFeatureConfigsRetrieveApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
};
export type FirmsCmsFeatureConfigsPartialUpdateApiResponse =
  /** status 200  */ CmsFeatureConfigSchema;
export type FirmsCmsFeatureConfigsPartialUpdateApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
  patchedUpdateCmsFeatureConfigSchema: UpdateCmsFeatureConfigSchema;
};
export type FirmsCmsFeatureConfigsDestroyApiResponse = unknown;
export type FirmsCmsFeatureConfigsDestroyApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
};
export type FirmsCmsFeatureConfigsDetailsApiResponse =
  /** status 200  */ CmsFeatureSyncFailuresSchema;
export type FirmsCmsFeatureConfigsDetailsApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
  /** How many recent bus runs to inspect (default 10, max 50) */
  runLimit?: number;
};
export type FirmsCmsFeatureConfigsTriggerApiResponse = unknown;
export type FirmsCmsFeatureConfigsTriggerApiArg = {
  firmId: string;
  id: string;
  /** The UUID of the CMS feature configuration */
  pk: string;
};
export type FirmsCmsFeatureConfigsSupportedFeaturesApiResponse =
  /** status 200  */ CmsSupportedFeaturesSchema;
export type FirmsCmsFeatureConfigsSupportedFeaturesApiArg = {
  firmId: string;
};
export type ExternalTaskRuleListApiResponse =
  /** status 200  */ ExternalTaskRuleSchema[];
export type ExternalTaskRuleListApiArg = {
  firmId: string;
  /** Filter by status (ENABLED or DISABLED) */
  status?: string;
};
export type ExternalTaskRuleCreateApiResponse =
  /** status 201  */ ExternalTaskRuleSchema;
export type ExternalTaskRuleCreateApiArg = {
  firmId: string;
  createExternalTaskRuleSchema: CreateExternalTaskRuleSchema;
};
export type ExternalTaskRuleRetrieveApiResponse =
  /** status 200  */ ExternalTaskRuleSchema;
export type ExternalTaskRuleRetrieveApiArg = {
  firmId: string;
  id: string;
};
export type ExternalTaskRulePartialUpdateApiResponse =
  /** status 200  */ ExternalTaskRuleSchema;
export type ExternalTaskRulePartialUpdateApiArg = {
  firmId: string;
  id: string;
  patchedUpdateExternalTaskRuleSchema: UpdateExternalTaskRuleSchema;
};
export type ExternalTaskRuleDestroyApiResponse = unknown;
export type ExternalTaskRuleDestroyApiArg = {
  firmId: string;
  id: string;
};
export type ExternalTaskRuleUpdateConfigApiResponse =
  /** status 200  */ ExternalTaskRuleSchema;
export type ExternalTaskRuleUpdateConfigApiArg = {
  firmId: string;
  id: string;
  updateExternalTaskRuleConfigSchema: UpdateExternalTaskRuleConfigSchema;
};
export type ExternalTaskRuleVersionsApiResponse =
  /** status 200  */ ExternalTaskRuleVersionSchema;
export type ExternalTaskRuleVersionsApiArg = {
  firmId: string;
  id: string;
};
export type ExternalTaskRuleStartBackfillApiResponse =
  /** status 201  */ RuleBackfillRunSchema;
export type ExternalTaskRuleStartBackfillApiArg = {
  firmId: string;
};
export type ExternalTaskRuleGetBackfillProgressApiResponse =
  /** status 200  */ BackfillProgressSchema;
export type ExternalTaskRuleGetBackfillProgressApiArg = {
  firmId: string;
  runId: string;
};
export type ExternalTaskRuleCancelBackfillApiResponse = unknown;
export type ExternalTaskRuleCancelBackfillApiArg = {
  firmId: string;
  runId: string;
};
export type ExternalTaskRuleListBackfillsApiResponse =
  /** status 200  */ ListRuleBackfillRunSchema;
export type ExternalTaskRuleListBackfillsApiArg = {
  firmId: string;
};
export type ExternalTaskRuleDistinctNamesApiResponse =
  /** status 200  */ ListDistinctExternalTaskNameSchema;
export type ExternalTaskRuleDistinctNamesApiArg = {
  /** Filter tasks where assignee name contains ANY of these strings */
  assigneeNames?: string[];
  /** Filter tasks where description contains ANY of these strings */
  descriptionContains?: string[];
  firmId: string;
  /** Minimum number of occurrences required (default: 2) */
  minCount?: number;
  /** Filter tasks where name contains ANY of these strings (case-insensitive) */
  nameContains?: string[];
  /** Exclude tasks where name contains ANY of these strings (case-insensitive) */
  nameNotContains?: string[];
  /** Filter tasks with ANY of these statuses (e.g., PENDING, IN_PROGRESS, COMPLETED) */
  statuses?: string[];
};
export type ExternalTaskRuleLiveTestApiResponse =
  /** status 200  */ LiveTestResultSchema;
export type ExternalTaskRuleLiveTestApiArg = {
  firmId: string;
  liveTestRequestSchema: LiveTestRequestSchema;
};
export type ExternalTaskRulePreviewExternalFilterApiResponse =
  /** status 200  */ PreviewExternalFilterResponseSchema;
export type ExternalTaskRulePreviewExternalFilterApiArg = {
  firmId: string;
  previewExternalFilterRequestSchema: PreviewExternalFilterRequestSchema;
};
export type ExternalTaskRulePreviewInternalFilterApiResponse =
  /** status 200  */ PreviewInternalFilterResponseSchema;
export type ExternalTaskRulePreviewInternalFilterApiArg = {
  firmId: string;
  previewInternalFilterRequestSchema: PreviewInternalFilterRequestSchema;
};
export type FilevineOrganizationLinkGetApiResponse =
  /** status 200  */ FirmFilevineOrganizationLinkSchema;
export type FilevineOrganizationLinkGetApiArg = {
  firmId: string;
};
export type FilevineOrganizationLinkCreateApiResponse =
  /** status 200  */ FirmFilevineOrganizationLinkSchema;
export type FilevineOrganizationLinkCreateApiArg = {
  firmId: string;
  linkFirmToOrganizationSchema: LinkFirmToOrganizationSchema;
};
export type FilevineOrganizationsListApiResponse =
  /** status 200  */ FilevineOrganizationWithLinkStatusSchema;
export type FilevineOrganizationsListApiArg = {
  firmId: string;
};
export type FilevineOrganizationUnlinkApiResponse =
  /** status 200  */ FirmFilevineOrganizationLinkSchema;
export type FilevineOrganizationUnlinkApiArg = {
  firmId: string;
};
export type FilevineProjectConfigsListApiResponse =
  /** status 200  */ FilevineProjectConfigWithSectionsSchema[];
export type FilevineProjectConfigsListApiArg = {
  firmId: string;
};
export type FilevineProjectConfigSyncTriggerApiResponse =
  /** status 200  */ ProjectConfigSyncResultSchema;
export type FilevineProjectConfigSyncTriggerApiArg = {
  firmId: string;
  triggerProjectConfigSyncSchema: TriggerProjectConfigSyncSchema;
};
export type FilevineSectionConfigsListApiResponse =
  /** status 200  */ FilevineSectionConfigDetailedSchema[];
export type FilevineSectionConfigsListApiArg = {
  firmId: string;
};
export type FilevineSectionConfigRetrieveApiResponse =
  /** status 200  */ FilevineSectionConfigDetailedSchema;
export type FilevineSectionConfigRetrieveApiArg = {
  firmId: string;
  id: string;
};
export type FilevineSectionConfigUpdateTypeApiResponse =
  /** status 200  */ FilevineSectionConfigDetailedSchema;
export type FilevineSectionConfigUpdateTypeApiArg = {
  firmId: string;
  id: string;
  patchedUpdateSectionTypeSchema: UpdateSectionTypeSchema;
};
export type FilevineContactRetrieveApiResponse =
  /** status 200  */ FilevineContactWithHandlesSchema;
export type FilevineContactRetrieveApiArg = {
  firmId: string;
  id: string;
};
export type FilevineContactByFilevineIdApiResponse =
  /** status 200  */ FilevineContactWithHandlesSchema;
export type FilevineContactByFilevineIdApiArg = {
  filevineId: string;
  firmId: string;
};
export type FilevineContactSearchApiResponse =
  /** status 200  */ FilevineContactSearchResponse;
export type FilevineContactSearchApiArg = {
  /** Filter to specific field types: full_name, first_name, last_name, phone, email, address */
  fieldTypes?: string[];
  firmId: string;
  /** Include archived contacts in results */
  includeArchived?: boolean;
  /** Maximum number of results (1-200) */
  limit?: number;
  /** Search query (name, phone, email, or address) */
  q: string;
};
export type PrintableFilevineSectionsListApiResponse =
  /** status 200  */ PrintableFilevineSectionSchema[];
export type PrintableFilevineSectionsListApiArg = {
  firmId: string;
};
export type PrintableFilevineSectionsCreateApiResponse =
  /** status 201  */ PrintableFilevineSectionSchema;
export type PrintableFilevineSectionsCreateApiArg = {
  firmId: string;
  createPrintableFilevineSectionSchema: CreatePrintableFilevineSectionSchema;
};
export type PrintableFilevineSectionsRetrieveApiResponse =
  /** status 200  */ PrintableFilevineSectionSchema;
export type PrintableFilevineSectionsRetrieveApiArg = {
  firmId: string;
  id: string;
};
export type PrintableFilevineSectionsUpdateApiResponse =
  /** status 200  */ PrintableFilevineSectionSchema;
export type PrintableFilevineSectionsUpdateApiArg = {
  firmId: string;
  id: string;
  updatePrintableFilevineSectionSchema: UpdatePrintableFilevineSectionSchema;
};
export type PrintableFilevineSectionsPartialUpdateApiResponse =
  /** status 200  */ PrintableFilevineSectionSchema;
export type PrintableFilevineSectionsPartialUpdateApiArg = {
  firmId: string;
  id: string;
  patchedUpdatePrintableFilevineSectionSchema: UpdatePrintableFilevineSectionSchema2;
};
export type PrintableFilevineSectionsDestroyApiResponse = unknown;
export type PrintableFilevineSectionsDestroyApiArg = {
  firmId: string;
  id: string;
};
export type GetFirmServicesApiResponse =
  /** status 200  */ FirmServiceWithWorkflowsSchema[];
export type GetFirmServicesApiArg = {
  firmId: string;
};
export type CreateFirmServiceApiResponse = /** status 201  */ FirmServiceSchema;
export type CreateFirmServiceApiArg = {
  firmId: string;
  createFirmServiceSchema: CreateFirmServiceSchema;
};
export type BulkUpdateFirmServiceWorkflowsApiResponse =
  /** status 200  */ FirmServiceWorkflowSchema;
export type BulkUpdateFirmServiceWorkflowsApiArg = {
  firmId: string;
  firmServiceId: string;
  updateFirmServiceWorkflowsRequest: UpdateFirmServiceWorkflowsRequest;
};
export type UpdateFirmServiceApiResponse = /** status 200  */ FirmServiceSchema;
export type UpdateFirmServiceApiArg = {
  firmId: string;
  /** Firm service ID */
  id: string;
  patchedUpdateFirmServiceSchema: UpdateFirmServiceSchema;
};
export type ActivateFirmServicesApiResponse =
  /** status 200  */ FirmServicesActivationResponse;
export type ActivateFirmServicesApiArg = {
  firmId: string;
};
export type GetFirmAutoCcEmailsApiResponse = /** status 200  */ {
  [key: string]: any;
};
export type GetFirmAutoCcEmailsApiArg = {
  firmId: string;
};
export type GetFirmServicesStatusApiResponse =
  /** status 200  */ FirmServicesStatusResponse;
export type GetFirmServicesStatusApiArg = {
  firmId: string;
};
export type ExecuteFirmServicesSyncApiResponse =
  /** status 200  */ BulkSyncExecuteResponse;
export type ExecuteFirmServicesSyncApiArg = {
  firmId: string;
};
export type PreviewFirmServicesSyncApiResponse =
  /** status 200  */ BulkSyncPreviewResponse;
export type PreviewFirmServicesSyncApiArg = {
  firmId: string;
};
export type FirmSmsTemplatesListApiResponse =
  /** status 200  */ SmsTemplateListResponse[];
export type FirmSmsTemplatesListApiArg = {
  /** Only return active templates (default: true) */
  activeOnly?: boolean;
  /** Filter templates by category */
  category?:
    | "Document Collection"
    | "Initial Treatment Assessment"
    | "Ongoing Treatment Check-In";
  firmId: string;
};
export type FirmSmsTemplatesCreateApiResponse =
  /** status 201  */ SmsTemplateSchema;
export type FirmSmsTemplatesCreateApiArg = {
  firmId: string;
  createSmsTemplateRequest: CreateSmsTemplateRequest;
};
export type FirmSmsTemplatesRetrieveApiResponse =
  /** status 200  */ SmsTemplateSchema;
export type FirmSmsTemplatesRetrieveApiArg = {
  firmId: string;
  id: string;
};
export type FirmSmsTemplatesPartialUpdateApiResponse =
  /** status 200  */ SmsTemplateSchema;
export type FirmSmsTemplatesPartialUpdateApiArg = {
  firmId: string;
  id: string;
  patchedUpdateSmsTemplateRequest: UpdateSmsTemplateRequest;
};
export type FirmSmsTemplatesDestroyApiResponse = unknown;
export type FirmSmsTemplatesDestroyApiArg = {
  firmId: string;
  id: string;
};
export type FirmTaskTemplatesListApiResponse =
  /** status 200  */ FirmTaskTemplateResponse[];
export type FirmTaskTemplatesListApiArg = {
  /** Firm ID */
  firmId: string;
};
export type FirmTaskTemplatesCreateApiResponse =
  /** status 201  */ FirmTaskTemplateResponse;
export type FirmTaskTemplatesCreateApiArg = {
  /** Firm ID */
  firmId: string;
  createFirmTaskTemplateSchema: CreateFirmTaskTemplateSchema;
};
export type FirmTaskTemplatesRetrieveApiResponse =
  /** status 200  */ FirmTaskTemplateResponse;
export type FirmTaskTemplatesRetrieveApiArg = {
  /** Firm ID */
  firmId: string;
  id: string;
};
export type FirmTaskTemplatesPartialUpdateApiResponse =
  /** status 200  */ FirmTaskTemplateResponse;
export type FirmTaskTemplatesPartialUpdateApiArg = {
  /** Firm ID */
  firmId: string;
  id: string;
  patchedUpdateFirmTaskTemplateSchema: UpdateFirmTaskTemplateSchema;
};
export type FirmTaskTemplatesApplyApiResponse =
  /** status 202  */ ApplyResponse;
export type FirmTaskTemplatesApplyApiArg = {
  /** Firm ID */
  firmId: string;
  id: string;
  body: Blob;
};
export type FirmTaskTemplatesApplyPreviewApiResponse =
  /** status 200  */ ApplyPreviewResponse;
export type FirmTaskTemplatesApplyPreviewApiArg = {
  /** Firm ID */
  firmId: string;
  id: string;
  body: Blob;
};
export type FirmTaskTemplatesOptionsApiResponse =
  /** status 200  */ OptionsResponse;
export type FirmTaskTemplatesOptionsApiArg = {
  /** Firm ID */
  firmId: string;
};
export type DocumentTemplatesListApiResponse =
  /** status 200  */ DocumentTemplateRead[];
export type DocumentTemplatesListApiArg = {
  /** UUID of the firm */
  firmId: string;
};
export type DocumentTemplatesCreateApiResponse =
  /** status 201  */ DocumentTemplateRead;
export type DocumentTemplatesCreateApiArg = {
  /** UUID of the firm */
  firmId: string;
  body: {
    name: string;
    template_type: string;
    file: Blob;
    attorney_id?: string;
  };
};
export type DocumentTemplateDownloadApiResponse = unknown;
export type DocumentTemplateDownloadApiArg = {
  /** UUID of the firm */
  firmId: string;
  /** UUID of the template */
  templateId: string;
};
export type DocumentTemplateEditorConfigApiResponse = unknown;
export type DocumentTemplateEditorConfigApiArg = {
  /** UUID of the firm */
  firmId: string;
  /** UUID of the template */
  templateId: string;
};
export type FirmDialpadNumbersListApiResponse =
  /** status 200 List of available Dialpad phone numbers */ DialpadPhoneNumberListResponse;
export type FirmDialpadNumbersListApiArg = {
  firmPk: string;
};
export type ListFirmRoleAssignmentsApiResponse =
  /** status 200  */ FirmRoleAssignmentDetailSchema[];
export type ListFirmRoleAssignmentsApiArg = {
  firmPk: string;
};
export type CreateFirmRoleAssignmentApiResponse =
  /** status 201  */ FirmRoleAssignmentDetailSchema;
export type CreateFirmRoleAssignmentApiArg = {
  firmPk: string;
  createFirmRoleAssignmentSchema: CreateFirmRoleAssignmentSchema;
};
export type GetFirmRoleAssignmentApiResponse =
  /** status 200  */ FirmRoleAssignmentDetailSchema;
export type GetFirmRoleAssignmentApiArg = {
  firmPk: string;
  /** A UUID string identifying this firm role assignment. */
  id: string;
};
export type UpdateFirmRoleAssignmentApiResponse =
  /** status 200  */ FirmRoleAssignmentDetailSchema;
export type UpdateFirmRoleAssignmentApiArg = {
  firmPk: string;
  /** A UUID string identifying this firm role assignment. */
  id: string;
  patchedUpdateFirmRoleAssignmentSchema: UpdateFirmRoleAssignmentSchema;
};
export type DeleteFirmRoleAssignmentApiResponse = unknown;
export type DeleteFirmRoleAssignmentApiArg = {
  firmPk: string;
  /** A UUID string identifying this firm role assignment. */
  id: string;
};
export type FirmsRetrieveApiResponse = /** status 200  */ FirmDetailLiteRead;
export type FirmsRetrieveApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
};
export type FirmsUpdateApiResponse = /** status 200  */ FirmRead;
export type FirmsUpdateApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  firm: FirmWrite;
};
export type FirmsPartialUpdateApiResponse = /** status 200  */ FirmRead;
export type FirmsPartialUpdateApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  patchedFirm: PatchedFirmWrite;
};
export type GetFirmAddressApiResponse = /** status 200  */ AddressSchema;
export type GetFirmAddressApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
};
export type SetFirmAddress2ApiResponse = /** status 200  */
  | AddressSchema
  | /** status 201  */ AddressSchema;
export type SetFirmAddress2ApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  createAddressSchema: CreateAddressSchema;
};
export type SetFirmAddressApiResponse = /** status 200  */
  | AddressSchema
  | /** status 201  */ AddressSchema;
export type SetFirmAddressApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  patchedCreateAddressSchema: CreateAddressSchema2;
};
export type SetClientPortalLogoApiResponse = /** status 200  */ FirmRead;
export type SetClientPortalLogoApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  setClientPortalLogoSchema: SetClientPortalLogoSchema;
};
export type SetFirmFooterApiResponse = /** status 200  */ FirmRead;
export type SetFirmFooterApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  setFirmFooterSchema: SetFirmFooterSchema;
};
export type SetFirmLogoApiResponse = /** status 200  */ FirmRead;
export type SetFirmLogoApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  setFirmLogoSchema: SetFirmLogoSchema;
};
export type SetFirmWatermarkApiResponse = /** status 200  */ FirmRead;
export type SetFirmWatermarkApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  setFirmWatermarkSchema: SetFirmWatermarkSchema;
};
export type SetHipaaAttorneyRelationshipFormApiResponse =
  /** status 200  */ FirmRead;
export type SetHipaaAttorneyRelationshipFormApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  setHipaaAttorneyRelationshipFormSchema: SetHipaaAttorneyRelationshipFormSchema;
};
export type SetHipaaReproductiveRightsAttestationApiResponse =
  /** status 200  */ FirmRead;
export type SetHipaaReproductiveRightsAttestationApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  setHipaaReproductiveRightsAttestationSchema: SetHipaaReproductiveRightsAttestationSchema;
};
export type SetLetterheadApiResponse = /** status 200  */ FirmRead;
export type SetLetterheadApiArg = {
  /** A UUID string identifying this firm. */
  id: string;
  setFirmLetterheadSchema: SetFirmLetterheadSchema;
};
export type GetServiceCatalogueApiResponse =
  /** status 200  */ ServiceCatalogueSchema;
export type GetServiceCatalogueApiArg = void;
export type FirmLite = {
  name: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  managing_paralegal_id: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
};
export type CustomUser = {
  first_name?: string;
  last_name?: string;
  /** Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only. */
  username: string;
  email: string;
  firm?: string | null;
  is_firm_admin?: boolean;
  /** Designates whether the user can log into this admin site. */
  is_staff?: boolean;
  /** User profile avatar image */
  avatar?: string | null;
  /** User signature image */
  signature?: string | null;
  extra?: any;
};
export type CustomUserRead = {
  id: string;
  first_name?: string;
  last_name?: string;
  display_name: string;
  /** Required. 150 characters or fewer. Letters, digits and @/./+/-/_ only. */
  username: string;
  email: string;
  firm?: string | null;
  is_firm_admin?: boolean;
  /** Designates whether the user can log into this admin site. */
  is_staff?: boolean;
  phone_numbers: {
    [key: string]: any;
  }[];
  /** User profile avatar image */
  avatar?: string | null;
  /** User signature image */
  signature?: string | null;
  extra?: any;
  created_at: string;
};
export type FirmLiteRead = {
  id: string;
  name: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  managing_paralegal: CustomUserRead | null;
  managing_paralegal_id: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  front_channel: {
    [key: string]: any;
  } | null;
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
};
export type Firm = {
  name: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  managing_paralegal_id: string | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Structured intake preferences organized by sections */
  intake_preferences?: any;
  /** Rich text field for case preferences and guidelines */
  case_preferences?: string | null;
  /** Rich text field for Head of Operations commentary and updates */
  commentary?: string | null;
  /** Rich text field for tracking errors and issues */
  errors?: string | null;
  /** Rich text field for derisking plans and strategies */
  plan_to_derisk?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
  /** Strategy for auto-assigning case managers to new matters
    
    * `disabled` - disabled
    * `round_robin` - round_robin */
  case_manager_assignment_strategy?: "disabled" | "round_robin";
  /** Unique subdomain for white-labeled portal (e.g., 'acmelaw' → acmelaw.portal.example.com) */
  client_portal_subdomain?: string | null;
  /** Primary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_primary_color?: string | null;
  /** Secondary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_secondary_color?: string | null;
};
export type Address = {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  /** * `AF` - Afghanistan
   * `AX` - Åland Islands
   * `AL` - Albania
   * `DZ` - Algeria
   * `AS` - American Samoa
   * `AD` - Andorra
   * `AO` - Angola
   * `AI` - Anguilla
   * `AQ` - Antarctica
   * `AG` - Antigua and Barbuda
   * `AR` - Argentina
   * `AM` - Armenia
   * `AW` - Aruba
   * `AU` - Australia
   * `AT` - Austria
   * `AZ` - Azerbaijan
   * `BS` - Bahamas
   * `BH` - Bahrain
   * `BD` - Bangladesh
   * `BB` - Barbados
   * `BY` - Belarus
   * `BE` - Belgium
   * `BZ` - Belize
   * `BJ` - Benin
   * `BM` - Bermuda
   * `BT` - Bhutan
   * `BO` - Bolivia, Plurinational State of
   * `BQ` - Bonaire, Sint Eustatius and Saba
   * `BA` - Bosnia and Herzegovina
   * `BW` - Botswana
   * `BV` - Bouvet Island
   * `BR` - Brazil
   * `IO` - British Indian Ocean Territory
   * `BN` - Brunei Darussalam
   * `BG` - Bulgaria
   * `BF` - Burkina Faso
   * `BI` - Burundi
   * `CV` - Cabo Verde
   * `KH` - Cambodia
   * `CM` - Cameroon
   * `CA` - Canada
   * `KY` - Cayman Islands
   * `CF` - Central African Republic
   * `TD` - Chad
   * `CL` - Chile
   * `CN` - China
   * `CX` - Christmas Island
   * `CC` - Cocos (Keeling) Islands
   * `CO` - Colombia
   * `KM` - Comoros
   * `CG` - Congo
   * `CD` - Congo, Democratic Republic of the
   * `CK` - Cook Islands
   * `CR` - Costa Rica
   * `CI` - Côte d'Ivoire
   * `HR` - Croatia
   * `CU` - Cuba
   * `CW` - Curaçao
   * `CY` - Cyprus
   * `CZ` - Czechia
   * `DK` - Denmark
   * `DJ` - Djibouti
   * `DM` - Dominica
   * `DO` - Dominican Republic
   * `EC` - Ecuador
   * `EG` - Egypt
   * `SV` - El Salvador
   * `GQ` - Equatorial Guinea
   * `ER` - Eritrea
   * `EE` - Estonia
   * `SZ` - Eswatini
   * `ET` - Ethiopia
   * `FK` - Falkland Islands (Malvinas)
   * `FO` - Faroe Islands
   * `FJ` - Fiji
   * `FI` - Finland
   * `FR` - France
   * `GF` - French Guiana
   * `PF` - French Polynesia
   * `TF` - French Southern Territories
   * `GA` - Gabon
   * `GM` - Gambia
   * `GE` - Georgia
   * `DE` - Germany
   * `GH` - Ghana
   * `GI` - Gibraltar
   * `GR` - Greece
   * `GL` - Greenland
   * `GD` - Grenada
   * `GP` - Guadeloupe
   * `GU` - Guam
   * `GT` - Guatemala
   * `GG` - Guernsey
   * `GN` - Guinea
   * `GW` - Guinea-Bissau
   * `GY` - Guyana
   * `HT` - Haiti
   * `HM` - Heard Island and McDonald Islands
   * `VA` - Holy See
   * `HN` - Honduras
   * `HK` - Hong Kong
   * `HU` - Hungary
   * `IS` - Iceland
   * `IN` - India
   * `ID` - Indonesia
   * `IR` - Iran, Islamic Republic of
   * `IQ` - Iraq
   * `IE` - Ireland
   * `IM` - Isle of Man
   * `IL` - Israel
   * `IT` - Italy
   * `JM` - Jamaica
   * `JP` - Japan
   * `JE` - Jersey
   * `JO` - Jordan
   * `KZ` - Kazakhstan
   * `KE` - Kenya
   * `KI` - Kiribati
   * `KP` - Korea, Democratic People's Republic of
   * `KR` - Korea, Republic of
   * `KW` - Kuwait
   * `KG` - Kyrgyzstan
   * `LA` - Lao People's Democratic Republic
   * `LV` - Latvia
   * `LB` - Lebanon
   * `LS` - Lesotho
   * `LR` - Liberia
   * `LY` - Libya
   * `LI` - Liechtenstein
   * `LT` - Lithuania
   * `LU` - Luxembourg
   * `MO` - Macao
   * `MG` - Madagascar
   * `MW` - Malawi
   * `MY` - Malaysia
   * `MV` - Maldives
   * `ML` - Mali
   * `MT` - Malta
   * `MH` - Marshall Islands
   * `MQ` - Martinique
   * `MR` - Mauritania
   * `MU` - Mauritius
   * `YT` - Mayotte
   * `MX` - Mexico
   * `FM` - Micronesia, Federated States of
   * `MD` - Moldova, Republic of
   * `MC` - Monaco
   * `MN` - Mongolia
   * `ME` - Montenegro
   * `MS` - Montserrat
   * `MA` - Morocco
   * `MZ` - Mozambique
   * `MM` - Myanmar
   * `NA` - Namibia
   * `NR` - Nauru
   * `NP` - Nepal
   * `NL` - Netherlands, Kingdom of the
   * `NC` - New Caledonia
   * `NZ` - New Zealand
   * `NI` - Nicaragua
   * `NE` - Niger
   * `NG` - Nigeria
   * `NU` - Niue
   * `NF` - Norfolk Island
   * `MK` - North Macedonia
   * `MP` - Northern Mariana Islands
   * `NO` - Norway
   * `OM` - Oman
   * `PK` - Pakistan
   * `PW` - Palau
   * `PS` - Palestine, State of
   * `PA` - Panama
   * `PG` - Papua New Guinea
   * `PY` - Paraguay
   * `PE` - Peru
   * `PH` - Philippines
   * `PN` - Pitcairn
   * `PL` - Poland
   * `PT` - Portugal
   * `PR` - Puerto Rico
   * `QA` - Qatar
   * `RE` - Réunion
   * `RO` - Romania
   * `RU` - Russian Federation
   * `RW` - Rwanda
   * `BL` - Saint Barthélemy
   * `SH` - Saint Helena, Ascension and Tristan da Cunha
   * `KN` - Saint Kitts and Nevis
   * `LC` - Saint Lucia
   * `MF` - Saint Martin (French part)
   * `PM` - Saint Pierre and Miquelon
   * `VC` - Saint Vincent and the Grenadines
   * `WS` - Samoa
   * `SM` - San Marino
   * `ST` - Sao Tome and Principe
   * `SA` - Saudi Arabia
   * `SN` - Senegal
   * `RS` - Serbia
   * `SC` - Seychelles
   * `SL` - Sierra Leone
   * `SG` - Singapore
   * `SX` - Sint Maarten (Dutch part)
   * `SK` - Slovakia
   * `SI` - Slovenia
   * `SB` - Solomon Islands
   * `SO` - Somalia
   * `ZA` - South Africa
   * `GS` - South Georgia and the South Sandwich Islands
   * `SS` - South Sudan
   * `ES` - Spain
   * `LK` - Sri Lanka
   * `SD` - Sudan
   * `SR` - Suriname
   * `SJ` - Svalbard and Jan Mayen
   * `SE` - Sweden
   * `CH` - Switzerland
   * `SY` - Syrian Arab Republic
   * `TW` - Taiwan, Province of China
   * `TJ` - Tajikistan
   * `TZ` - Tanzania, United Republic of
   * `TH` - Thailand
   * `TL` - Timor-Leste
   * `TG` - Togo
   * `TK` - Tokelau
   * `TO` - Tonga
   * `TT` - Trinidad and Tobago
   * `TN` - Tunisia
   * `TR` - Türkiye
   * `TM` - Turkmenistan
   * `TC` - Turks and Caicos Islands
   * `TV` - Tuvalu
   * `UG` - Uganda
   * `UA` - Ukraine
   * `AE` - United Arab Emirates
   * `GB` - United Kingdom of Great Britain and Northern Ireland
   * `US` - United States of America
   * `UM` - United States Minor Outlying Islands
   * `UY` - Uruguay
   * `UZ` - Uzbekistan
   * `VU` - Vanuatu
   * `VE` - Venezuela, Bolivarian Republic of
   * `VN` - Viet Nam
   * `VG` - Virgin Islands (British)
   * `VI` - Virgin Islands (U.S.)
   * `WF` - Wallis and Futuna
   * `EH` - Western Sahara
   * `YE` - Yemen
   * `ZM` - Zambia
   * `ZW` - Zimbabwe */
  country?:
    | (
        | "AF"
        | "AX"
        | "AL"
        | "DZ"
        | "AS"
        | "AD"
        | "AO"
        | "AI"
        | "AQ"
        | "AG"
        | "AR"
        | "AM"
        | "AW"
        | "AU"
        | "AT"
        | "AZ"
        | "BS"
        | "BH"
        | "BD"
        | "BB"
        | "BY"
        | "BE"
        | "BZ"
        | "BJ"
        | "BM"
        | "BT"
        | "BO"
        | "BQ"
        | "BA"
        | "BW"
        | "BV"
        | "BR"
        | "IO"
        | "BN"
        | "BG"
        | "BF"
        | "BI"
        | "CV"
        | "KH"
        | "CM"
        | "CA"
        | "KY"
        | "CF"
        | "TD"
        | "CL"
        | "CN"
        | "CX"
        | "CC"
        | "CO"
        | "KM"
        | "CG"
        | "CD"
        | "CK"
        | "CR"
        | "CI"
        | "HR"
        | "CU"
        | "CW"
        | "CY"
        | "CZ"
        | "DK"
        | "DJ"
        | "DM"
        | "DO"
        | "EC"
        | "EG"
        | "SV"
        | "GQ"
        | "ER"
        | "EE"
        | "SZ"
        | "ET"
        | "FK"
        | "FO"
        | "FJ"
        | "FI"
        | "FR"
        | "GF"
        | "PF"
        | "TF"
        | "GA"
        | "GM"
        | "GE"
        | "DE"
        | "GH"
        | "GI"
        | "GR"
        | "GL"
        | "GD"
        | "GP"
        | "GU"
        | "GT"
        | "GG"
        | "GN"
        | "GW"
        | "GY"
        | "HT"
        | "HM"
        | "VA"
        | "HN"
        | "HK"
        | "HU"
        | "IS"
        | "IN"
        | "ID"
        | "IR"
        | "IQ"
        | "IE"
        | "IM"
        | "IL"
        | "IT"
        | "JM"
        | "JP"
        | "JE"
        | "JO"
        | "KZ"
        | "KE"
        | "KI"
        | "KP"
        | "KR"
        | "KW"
        | "KG"
        | "LA"
        | "LV"
        | "LB"
        | "LS"
        | "LR"
        | "LY"
        | "LI"
        | "LT"
        | "LU"
        | "MO"
        | "MG"
        | "MW"
        | "MY"
        | "MV"
        | "ML"
        | "MT"
        | "MH"
        | "MQ"
        | "MR"
        | "MU"
        | "YT"
        | "MX"
        | "FM"
        | "MD"
        | "MC"
        | "MN"
        | "ME"
        | "MS"
        | "MA"
        | "MZ"
        | "MM"
        | "NA"
        | "NR"
        | "NP"
        | "NL"
        | "NC"
        | "NZ"
        | "NI"
        | "NE"
        | "NG"
        | "NU"
        | "NF"
        | "MK"
        | "MP"
        | "NO"
        | "OM"
        | "PK"
        | "PW"
        | "PS"
        | "PA"
        | "PG"
        | "PY"
        | "PE"
        | "PH"
        | "PN"
        | "PL"
        | "PT"
        | "PR"
        | "QA"
        | "RE"
        | "RO"
        | "RU"
        | "RW"
        | "BL"
        | "SH"
        | "KN"
        | "LC"
        | "MF"
        | "PM"
        | "VC"
        | "WS"
        | "SM"
        | "ST"
        | "SA"
        | "SN"
        | "RS"
        | "SC"
        | "SL"
        | "SG"
        | "SX"
        | "SK"
        | "SI"
        | "SB"
        | "SO"
        | "ZA"
        | "GS"
        | "SS"
        | "ES"
        | "LK"
        | "SD"
        | "SR"
        | "SJ"
        | "SE"
        | "CH"
        | "SY"
        | "TW"
        | "TJ"
        | "TZ"
        | "TH"
        | "TL"
        | "TG"
        | "TK"
        | "TO"
        | "TT"
        | "TN"
        | "TR"
        | "TM"
        | "TC"
        | "TV"
        | "UG"
        | "UA"
        | "AE"
        | "GB"
        | "US"
        | "UM"
        | "UY"
        | "UZ"
        | "VU"
        | "VE"
        | "VN"
        | "VG"
        | "VI"
        | "WF"
        | "EH"
        | "YE"
        | "ZM"
        | "ZW"
        | ""
        | null
      )
    | null;
};
export type AddressRead = {
  id: string;
  created_at: string;
  modified_at: string;
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  /** * `AF` - Afghanistan
   * `AX` - Åland Islands
   * `AL` - Albania
   * `DZ` - Algeria
   * `AS` - American Samoa
   * `AD` - Andorra
   * `AO` - Angola
   * `AI` - Anguilla
   * `AQ` - Antarctica
   * `AG` - Antigua and Barbuda
   * `AR` - Argentina
   * `AM` - Armenia
   * `AW` - Aruba
   * `AU` - Australia
   * `AT` - Austria
   * `AZ` - Azerbaijan
   * `BS` - Bahamas
   * `BH` - Bahrain
   * `BD` - Bangladesh
   * `BB` - Barbados
   * `BY` - Belarus
   * `BE` - Belgium
   * `BZ` - Belize
   * `BJ` - Benin
   * `BM` - Bermuda
   * `BT` - Bhutan
   * `BO` - Bolivia, Plurinational State of
   * `BQ` - Bonaire, Sint Eustatius and Saba
   * `BA` - Bosnia and Herzegovina
   * `BW` - Botswana
   * `BV` - Bouvet Island
   * `BR` - Brazil
   * `IO` - British Indian Ocean Territory
   * `BN` - Brunei Darussalam
   * `BG` - Bulgaria
   * `BF` - Burkina Faso
   * `BI` - Burundi
   * `CV` - Cabo Verde
   * `KH` - Cambodia
   * `CM` - Cameroon
   * `CA` - Canada
   * `KY` - Cayman Islands
   * `CF` - Central African Republic
   * `TD` - Chad
   * `CL` - Chile
   * `CN` - China
   * `CX` - Christmas Island
   * `CC` - Cocos (Keeling) Islands
   * `CO` - Colombia
   * `KM` - Comoros
   * `CG` - Congo
   * `CD` - Congo, Democratic Republic of the
   * `CK` - Cook Islands
   * `CR` - Costa Rica
   * `CI` - Côte d'Ivoire
   * `HR` - Croatia
   * `CU` - Cuba
   * `CW` - Curaçao
   * `CY` - Cyprus
   * `CZ` - Czechia
   * `DK` - Denmark
   * `DJ` - Djibouti
   * `DM` - Dominica
   * `DO` - Dominican Republic
   * `EC` - Ecuador
   * `EG` - Egypt
   * `SV` - El Salvador
   * `GQ` - Equatorial Guinea
   * `ER` - Eritrea
   * `EE` - Estonia
   * `SZ` - Eswatini
   * `ET` - Ethiopia
   * `FK` - Falkland Islands (Malvinas)
   * `FO` - Faroe Islands
   * `FJ` - Fiji
   * `FI` - Finland
   * `FR` - France
   * `GF` - French Guiana
   * `PF` - French Polynesia
   * `TF` - French Southern Territories
   * `GA` - Gabon
   * `GM` - Gambia
   * `GE` - Georgia
   * `DE` - Germany
   * `GH` - Ghana
   * `GI` - Gibraltar
   * `GR` - Greece
   * `GL` - Greenland
   * `GD` - Grenada
   * `GP` - Guadeloupe
   * `GU` - Guam
   * `GT` - Guatemala
   * `GG` - Guernsey
   * `GN` - Guinea
   * `GW` - Guinea-Bissau
   * `GY` - Guyana
   * `HT` - Haiti
   * `HM` - Heard Island and McDonald Islands
   * `VA` - Holy See
   * `HN` - Honduras
   * `HK` - Hong Kong
   * `HU` - Hungary
   * `IS` - Iceland
   * `IN` - India
   * `ID` - Indonesia
   * `IR` - Iran, Islamic Republic of
   * `IQ` - Iraq
   * `IE` - Ireland
   * `IM` - Isle of Man
   * `IL` - Israel
   * `IT` - Italy
   * `JM` - Jamaica
   * `JP` - Japan
   * `JE` - Jersey
   * `JO` - Jordan
   * `KZ` - Kazakhstan
   * `KE` - Kenya
   * `KI` - Kiribati
   * `KP` - Korea, Democratic People's Republic of
   * `KR` - Korea, Republic of
   * `KW` - Kuwait
   * `KG` - Kyrgyzstan
   * `LA` - Lao People's Democratic Republic
   * `LV` - Latvia
   * `LB` - Lebanon
   * `LS` - Lesotho
   * `LR` - Liberia
   * `LY` - Libya
   * `LI` - Liechtenstein
   * `LT` - Lithuania
   * `LU` - Luxembourg
   * `MO` - Macao
   * `MG` - Madagascar
   * `MW` - Malawi
   * `MY` - Malaysia
   * `MV` - Maldives
   * `ML` - Mali
   * `MT` - Malta
   * `MH` - Marshall Islands
   * `MQ` - Martinique
   * `MR` - Mauritania
   * `MU` - Mauritius
   * `YT` - Mayotte
   * `MX` - Mexico
   * `FM` - Micronesia, Federated States of
   * `MD` - Moldova, Republic of
   * `MC` - Monaco
   * `MN` - Mongolia
   * `ME` - Montenegro
   * `MS` - Montserrat
   * `MA` - Morocco
   * `MZ` - Mozambique
   * `MM` - Myanmar
   * `NA` - Namibia
   * `NR` - Nauru
   * `NP` - Nepal
   * `NL` - Netherlands, Kingdom of the
   * `NC` - New Caledonia
   * `NZ` - New Zealand
   * `NI` - Nicaragua
   * `NE` - Niger
   * `NG` - Nigeria
   * `NU` - Niue
   * `NF` - Norfolk Island
   * `MK` - North Macedonia
   * `MP` - Northern Mariana Islands
   * `NO` - Norway
   * `OM` - Oman
   * `PK` - Pakistan
   * `PW` - Palau
   * `PS` - Palestine, State of
   * `PA` - Panama
   * `PG` - Papua New Guinea
   * `PY` - Paraguay
   * `PE` - Peru
   * `PH` - Philippines
   * `PN` - Pitcairn
   * `PL` - Poland
   * `PT` - Portugal
   * `PR` - Puerto Rico
   * `QA` - Qatar
   * `RE` - Réunion
   * `RO` - Romania
   * `RU` - Russian Federation
   * `RW` - Rwanda
   * `BL` - Saint Barthélemy
   * `SH` - Saint Helena, Ascension and Tristan da Cunha
   * `KN` - Saint Kitts and Nevis
   * `LC` - Saint Lucia
   * `MF` - Saint Martin (French part)
   * `PM` - Saint Pierre and Miquelon
   * `VC` - Saint Vincent and the Grenadines
   * `WS` - Samoa
   * `SM` - San Marino
   * `ST` - Sao Tome and Principe
   * `SA` - Saudi Arabia
   * `SN` - Senegal
   * `RS` - Serbia
   * `SC` - Seychelles
   * `SL` - Sierra Leone
   * `SG` - Singapore
   * `SX` - Sint Maarten (Dutch part)
   * `SK` - Slovakia
   * `SI` - Slovenia
   * `SB` - Solomon Islands
   * `SO` - Somalia
   * `ZA` - South Africa
   * `GS` - South Georgia and the South Sandwich Islands
   * `SS` - South Sudan
   * `ES` - Spain
   * `LK` - Sri Lanka
   * `SD` - Sudan
   * `SR` - Suriname
   * `SJ` - Svalbard and Jan Mayen
   * `SE` - Sweden
   * `CH` - Switzerland
   * `SY` - Syrian Arab Republic
   * `TW` - Taiwan, Province of China
   * `TJ` - Tajikistan
   * `TZ` - Tanzania, United Republic of
   * `TH` - Thailand
   * `TL` - Timor-Leste
   * `TG` - Togo
   * `TK` - Tokelau
   * `TO` - Tonga
   * `TT` - Trinidad and Tobago
   * `TN` - Tunisia
   * `TR` - Türkiye
   * `TM` - Turkmenistan
   * `TC` - Turks and Caicos Islands
   * `TV` - Tuvalu
   * `UG` - Uganda
   * `UA` - Ukraine
   * `AE` - United Arab Emirates
   * `GB` - United Kingdom of Great Britain and Northern Ireland
   * `US` - United States of America
   * `UM` - United States Minor Outlying Islands
   * `UY` - Uruguay
   * `UZ` - Uzbekistan
   * `VU` - Vanuatu
   * `VE` - Venezuela, Bolivarian Republic of
   * `VN` - Viet Nam
   * `VG` - Virgin Islands (British)
   * `VI` - Virgin Islands (U.S.)
   * `WF` - Wallis and Futuna
   * `EH` - Western Sahara
   * `YE` - Yemen
   * `ZM` - Zambia
   * `ZW` - Zimbabwe */
  country?:
    | (
        | "AF"
        | "AX"
        | "AL"
        | "DZ"
        | "AS"
        | "AD"
        | "AO"
        | "AI"
        | "AQ"
        | "AG"
        | "AR"
        | "AM"
        | "AW"
        | "AU"
        | "AT"
        | "AZ"
        | "BS"
        | "BH"
        | "BD"
        | "BB"
        | "BY"
        | "BE"
        | "BZ"
        | "BJ"
        | "BM"
        | "BT"
        | "BO"
        | "BQ"
        | "BA"
        | "BW"
        | "BV"
        | "BR"
        | "IO"
        | "BN"
        | "BG"
        | "BF"
        | "BI"
        | "CV"
        | "KH"
        | "CM"
        | "CA"
        | "KY"
        | "CF"
        | "TD"
        | "CL"
        | "CN"
        | "CX"
        | "CC"
        | "CO"
        | "KM"
        | "CG"
        | "CD"
        | "CK"
        | "CR"
        | "CI"
        | "HR"
        | "CU"
        | "CW"
        | "CY"
        | "CZ"
        | "DK"
        | "DJ"
        | "DM"
        | "DO"
        | "EC"
        | "EG"
        | "SV"
        | "GQ"
        | "ER"
        | "EE"
        | "SZ"
        | "ET"
        | "FK"
        | "FO"
        | "FJ"
        | "FI"
        | "FR"
        | "GF"
        | "PF"
        | "TF"
        | "GA"
        | "GM"
        | "GE"
        | "DE"
        | "GH"
        | "GI"
        | "GR"
        | "GL"
        | "GD"
        | "GP"
        | "GU"
        | "GT"
        | "GG"
        | "GN"
        | "GW"
        | "GY"
        | "HT"
        | "HM"
        | "VA"
        | "HN"
        | "HK"
        | "HU"
        | "IS"
        | "IN"
        | "ID"
        | "IR"
        | "IQ"
        | "IE"
        | "IM"
        | "IL"
        | "IT"
        | "JM"
        | "JP"
        | "JE"
        | "JO"
        | "KZ"
        | "KE"
        | "KI"
        | "KP"
        | "KR"
        | "KW"
        | "KG"
        | "LA"
        | "LV"
        | "LB"
        | "LS"
        | "LR"
        | "LY"
        | "LI"
        | "LT"
        | "LU"
        | "MO"
        | "MG"
        | "MW"
        | "MY"
        | "MV"
        | "ML"
        | "MT"
        | "MH"
        | "MQ"
        | "MR"
        | "MU"
        | "YT"
        | "MX"
        | "FM"
        | "MD"
        | "MC"
        | "MN"
        | "ME"
        | "MS"
        | "MA"
        | "MZ"
        | "MM"
        | "NA"
        | "NR"
        | "NP"
        | "NL"
        | "NC"
        | "NZ"
        | "NI"
        | "NE"
        | "NG"
        | "NU"
        | "NF"
        | "MK"
        | "MP"
        | "NO"
        | "OM"
        | "PK"
        | "PW"
        | "PS"
        | "PA"
        | "PG"
        | "PY"
        | "PE"
        | "PH"
        | "PN"
        | "PL"
        | "PT"
        | "PR"
        | "QA"
        | "RE"
        | "RO"
        | "RU"
        | "RW"
        | "BL"
        | "SH"
        | "KN"
        | "LC"
        | "MF"
        | "PM"
        | "VC"
        | "WS"
        | "SM"
        | "ST"
        | "SA"
        | "SN"
        | "RS"
        | "SC"
        | "SL"
        | "SG"
        | "SX"
        | "SK"
        | "SI"
        | "SB"
        | "SO"
        | "ZA"
        | "GS"
        | "SS"
        | "ES"
        | "LK"
        | "SD"
        | "SR"
        | "SJ"
        | "SE"
        | "CH"
        | "SY"
        | "TW"
        | "TJ"
        | "TZ"
        | "TH"
        | "TL"
        | "TG"
        | "TK"
        | "TO"
        | "TT"
        | "TN"
        | "TR"
        | "TM"
        | "TC"
        | "TV"
        | "UG"
        | "UA"
        | "AE"
        | "GB"
        | "US"
        | "UM"
        | "UY"
        | "UZ"
        | "VU"
        | "VE"
        | "VN"
        | "VG"
        | "VI"
        | "WF"
        | "EH"
        | "YE"
        | "ZM"
        | "ZW"
        | ""
        | null
      )
    | null;
};
export type FirmRead = {
  id: string;
  name: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  managing_paralegal: CustomUserRead | null;
  managing_paralegal_id: string | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  created_at: string;
  modified_at: string;
  hipaa_cover_sheet_template: string | null;
  hipaa_reproductive_rights_attestation_url: string | null;
  hipaa_attorney_relationship_form_url: string | null;
  /** Get all HIPAA cover sheet templates for this firm via v2 Template model. */
  hipaa_cover_sheet_templates: {
    [key: string]: any;
  }[];
  /** Get the ID of the default HIPAA template for this firm via v2 Template model. */
  default_hipaa_template_id: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Structured intake preferences organized by sections */
  intake_preferences?: any;
  /** Rich text field for case preferences and guidelines */
  case_preferences?: string | null;
  /** Rich text field for Head of Operations commentary and updates */
  commentary?: string | null;
  /** Rich text field for tracking errors and issues */
  errors?: string | null;
  /** Rich text field for derisking plans and strategies */
  plan_to_derisk?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  front_channel: {
    [key: string]: any;
  } | null;
  address: AddressRead | null;
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
  /** Strategy for auto-assigning case managers to new matters
    
    * `disabled` - disabled
    * `round_robin` - round_robin */
  case_manager_assignment_strategy?: "disabled" | "round_robin";
  /** Unique subdomain for white-labeled portal (e.g., 'acmelaw' → acmelaw.portal.example.com) */
  client_portal_subdomain?: string | null;
  client_portal_logo_url: string | null;
  /** Primary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_primary_color?: string | null;
  /** Secondary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_secondary_color?: string | null;
  letterhead_url: string | null;
  firm_footer_url: string | null;
  firm_logo_url: string | null;
  firm_watermark_url: string | null;
};
export type FirmWrite = {
  name: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  managing_paralegal_id: string | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Structured intake preferences organized by sections */
  intake_preferences?: any;
  /** Rich text field for case preferences and guidelines */
  case_preferences?: string | null;
  /** Rich text field for Head of Operations commentary and updates */
  commentary?: string | null;
  /** Rich text field for tracking errors and issues */
  errors?: string | null;
  /** Rich text field for derisking plans and strategies */
  plan_to_derisk?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  front_channel_front_id?: string | null;
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
  /** Strategy for auto-assigning case managers to new matters
    
    * `disabled` - disabled
    * `round_robin` - round_robin */
  case_manager_assignment_strategy?: "disabled" | "round_robin";
  /** Unique subdomain for white-labeled portal (e.g., 'acmelaw' → acmelaw.portal.example.com) */
  client_portal_subdomain?: string | null;
  /** Primary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_primary_color?: string | null;
  /** Secondary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_secondary_color?: string | null;
};
export type BlobSinkActionTargetSystem =
  | "FILEVINE"
  | "LITIFY"
  | "NEOS"
  | "CLIO"
  | "SMARTADVOCATE"
  | "DROPBOX"
  | "CASEPEER"
  | "MYCASE"
  | "GOOGLE_DRIVE";
export type BlobSinkApprovalMode = "AUTOMATIC" | "HUMAN";
export type BlobSinkConfigSchema = {
  id: string | string;
  firm_id: string | string;
  target_system: BlobSinkActionTargetSystem;
  is_enabled: boolean;
  default_approval_mode: BlobSinkApprovalMode;
  file_location_prompt: string;
  push_window_start_time?: string | null;
  push_window_end_time?: string | null;
  push_window_timezone?: string | null;
};
export type CreateBlobSinkConfigSchema = {
  firm_id: string | string;
  target_system: BlobSinkActionTargetSystem;
  is_enabled?: boolean;
  default_approval_mode?: BlobSinkApprovalMode;
  file_location_prompt?: string;
  push_window_start_time?: string | null;
  push_window_end_time?: string | null;
  push_window_timezone?: string | null;
};
export type UpdateBlobSinkConfigSchema = {
  is_enabled?: boolean | null;
  default_approval_mode?: BlobSinkApprovalMode;
  file_location_prompt?: string | null;
  push_window_start_time?: string | null;
  push_window_end_time?: string | null;
  push_window_timezone?: string | null;
};
export type BlobSinkPromptPreviewRunStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED";
export type BlobSinkPromptPreviewRunSchema = {
  id: string | string;
  firm_id: string | string;
  config_id: string | string;
  base_run_id?: (string | string) | null;
  target_system: BlobSinkActionTargetSystem;
  prompt_snapshot: string;
  preview_size: number;
  status: BlobSinkPromptPreviewRunStatus;
  total_count: number;
  processed_count: number;
  successful_count: number;
  failed_count: number;
  sampled_matter_ids?: (string | string)[];
  sampled_blob_ids?: (string | string)[];
  workflow_id?: string | null;
  error_message?: string | null;
  created_by_id?: (string | string) | null;
  created_at: string;
  modified_at: string;
};
export type CreateBlobSinkPromptPreviewRunSchema = {
  prompt: string;
  preview_size?: number;
  base_run_id?: (string | string) | null;
};
export type BlobSinkPromptPreviewResultStatus = "SUCCESS" | "FAILED";
export type BlobSinkPromptPreviewResultSchema = {
  id: string | string;
  run_id: string | string;
  action_id?: (string | string) | null;
  matter_id: string | string;
  blob_id: string | string;
  status: BlobSinkPromptPreviewResultStatus;
  current_path?: string | null;
  current_name?: string | null;
  proposed_path?: string | null;
  proposed_name?: string | null;
  reasoning?: string | null;
  confidence?: number | null;
  error_message?: string | null;
  created_at: string;
  modified_at: string;
};
export type BlobSinkPromptPreviewRunDetailsSchema = {
  id: string | string;
  firm_id: string | string;
  config_id: string | string;
  base_run_id?: (string | string) | null;
  target_system: BlobSinkActionTargetSystem;
  prompt_snapshot: string;
  preview_size: number;
  status: BlobSinkPromptPreviewRunStatus;
  total_count: number;
  processed_count: number;
  successful_count: number;
  failed_count: number;
  sampled_matter_ids?: (string | string)[];
  sampled_blob_ids?: (string | string)[];
  workflow_id?: string | null;
  error_message?: string | null;
  created_by_id?: (string | string) | null;
  created_at: string;
  modified_at: string;
  results?: BlobSinkPromptPreviewResultSchema[];
};
export type GuessConventionResponseSchema = {
  convention: string | null;
  target_system: BlobSinkActionTargetSystem;
};
export type FirmCaseManagerSchema = {
  /** User ID of the case manager */
  id: string | string;
  /** Display name of the case manager */
  display_name: string;
  /** Email of the case manager */
  email: string;
  /** Whether this CM is eligible for auto-assignment (from FirmRoleAssignment config) */
  case_auto_assign?: boolean;
};
export type ExternalCmsType =
  | "casepeer"
  | "clio"
  | "dropbox"
  | "filevine"
  | "google_drive"
  | "mycase"
  | "neos"
  | "litify"
  | "smartadvocate";
export type CmsFeature =
  | "task_sync"
  | "document_source"
  | "contact_sync"
  | "entity_sync_pipeline"
  | "entity_auto_push";
export type ScheduleRange = {
  start: number;
  end?: number;
  step?: number;
};
export type ScheduleCalendarSpec = {
  second?: ScheduleRange[];
  minute?: ScheduleRange[];
  hour?: ScheduleRange[];
  day_of_month?: ScheduleRange[];
  month?: ScheduleRange[];
  year?: ScheduleRange[];
  day_of_week?: ScheduleRange[];
  comment?: string | null;
};
export type ScheduleIntervalSpec = {
  every: string;
  offset?: string | null;
};
export type ScheduleSpec = {
  calendars?: ScheduleCalendarSpec[];
  intervals?: ScheduleIntervalSpec[];
  cron_expressions?: string[];
  skip?: ScheduleCalendarSpec[];
  start_at?: string | null;
  end_at?: string | null;
  jitter?: string | null;
  time_zone_name?: string | null;
};
export type CmsFeatureConfigSchema = {
  id: string | string;
  firm_id: string | string;
  cms_type: ExternalCmsType;
  feature: CmsFeature;
  enabled: boolean;
  description: string;
  schedule_spec: ScheduleSpec;
  temporal_schedule_id: string | null;
  last_synced_at: string | null;
  last_sync_error: string | null;
  created_at: string;
  modified_at: string;
};
export type CreateCmsFeatureConfigSchema = {
  firm_id: string | string;
  cms_type: ExternalCmsType;
  feature: CmsFeature;
  enabled?: boolean;
  description?: string;
  schedule_spec?: ScheduleSpec;
};
export type UpdateCmsFeatureConfigSchema = {
  enabled?: boolean | null;
  description?: string | null;
  schedule_spec?: ScheduleSpec;
};
export type CmsFeatureMatterFailureSchema = {
  matter_id: string | string;
  error: string;
};
export type CmsFeatureSyncRunStatusSchema = {
  bus_run_id: string;
  bus_status: string;
  bus_started_at?: string | null;
  bus_closed_at?: string | null;
  child_workflow_id?: string | null;
  child_run_id?: string | null;
  child_workflow_type?: string | null;
  child_status?: string | null;
  feature_completed?: boolean;
  feature_failed?: boolean;
  feature_error?: string | null;
  matter_failures?: CmsFeatureMatterFailureSchema[];
};
export type CmsFeatureSyncFailuresSchema = {
  config_id: string | string;
  bus_workflow_id: string;
  cms_type: ExternalCmsType;
  feature: CmsFeature;
  runs: CmsFeatureSyncRunStatusSchema[];
};
export type CmsSupportedFeaturesSchema = {
  supported_features: {
    [key: string]: CmsFeature[];
  };
};
export type ExternalTaskRuleStatus = "ENABLED" | "DISABLED";
export type ExternalTaskRuleType = "LINKING" | "EXCLUSION";
export type InternalTaskFilter = {
  /** Match if task belongs to ANY of these task groups. */
  task_group_ids?: (string | string)[];
  /** Match if task has ANY of these task types. */
  task_type_ids?: (string | string)[];
  /** Match if task was created from ANY of these firm task templates. */
  firm_task_template_ids?: (string | string)[];
};
export type ExternalTaskStatus =
  | "PENDING"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "NOT_APPLICABLE";
export type ExternalTaskFilter = {
  /** Match if task name contains ANY of these strings (case-insensitive). */
  name_contains?: string[];
  /** Exclude if task name contains ANY of these strings (case-insensitive). */
  name_not_contains?: string[];
  /** Match if task description contains ANY of these strings. */
  description_contains?: string[];
  /** Match if task status is ANY of these. Empty means all statuses. */
  statuses?: ExternalTaskStatus[];
  /** Match if assignee name contains ANY of these strings. */
  assignee_names?: string[];
};
export type RuleConfig = {
  /** Criteria for matching internal tasks. */
  internal_task_filter?: InternalTaskFilter;
  /** Criteria for matching external tasks. */
  external_task_filter?: ExternalTaskFilter;
};
export type ExternalTaskRuleVersionSchema = {
  id: string | string;
  rule_id: string | string;
  version_number: number;
  is_active: boolean;
  config: RuleConfig;
  created_by_id: (string | string) | null;
  created_at: string;
  modified_at: string;
};
export type ExternalTaskRuleSchema = {
  id: string | string;
  firm_id: string | string;
  name: string;
  description: string | null;
  status: ExternalTaskRuleStatus;
  rule_type: ExternalTaskRuleType;
  match_sla?: string | null;
  active_version?: ExternalTaskRuleVersionSchema;
  created_at: string;
  modified_at: string;
};
export type CreateExternalTaskRuleSchema = {
  firm_id: string | string;
  name: string;
  description?: string | null;
  rule_type?: ExternalTaskRuleType;
  match_sla?: string | null;
  internal_task_filter: InternalTaskFilter;
  external_task_filter: ExternalTaskFilter;
};
export type UpdateExternalTaskRuleSchema = {
  name?: string | null;
  description?: string | null;
  status?: ExternalTaskRuleStatus;
  rule_type?: ExternalTaskRuleType;
  match_sla?: string | null;
};
export type UpdateExternalTaskRuleConfigSchema = {
  internal_task_filter: InternalTaskFilter;
  external_task_filter: ExternalTaskFilter;
};
export type RuleBackfillRunStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "CANCELLED"
  | "FAILED";
export type RuleBackfillRunSchema = {
  id: string | string;
  firm_id: string | string;
  status: RuleBackfillRunStatus;
  total_matters: number;
  processed_matters: number;
  total_links_created: number;
  workflow_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  triggered_by_id: (string | string) | null;
  error_message: string | null;
  created_at: string;
};
export type BackfillProgressSchema = {
  run_id: string | string;
  status: RuleBackfillRunStatus;
  processed_matters: number;
  total_matters: number;
  total_links_created: number;
  is_running: boolean;
};
export type ListRuleBackfillRunSchema = RuleBackfillRunSchema[];
export type DistinctExternalTaskNameSchema = {
  name: string;
  count: number;
};
export type ListDistinctExternalTaskNameSchema =
  DistinctExternalTaskNameSchema[];
export type TriggerTaskType = "EXTERNAL" | "INTERNAL";
export type LiveTestMatchedTaskSchema = {
  task_id: string | string;
  task_name: string;
  task_type: TriggerTaskType;
  would_create_link: boolean;
};
export type LiveTestMatchSchema = {
  trigger_task_id: string | string;
  trigger_task_name: string;
  trigger_task_type: TriggerTaskType;
  matter_id: string | string;
  matter_name?: string | null;
  matched_tasks: LiveTestMatchedTaskSchema[];
};
export type LiveTestExcludedTaskSchema = {
  task_id: string | string;
  task_name: string;
  matter_id: string | string;
  matter_name?: string | null;
  already_excluded?: boolean;
};
export type LiveTestResultSchema = {
  total_trigger_tasks_evaluated: number;
  total_matches: number;
  total_links_would_create?: number;
  matches?: LiveTestMatchSchema[];
  total_tasks_would_exclude?: number;
  excluded_tasks?: LiveTestExcludedTaskSchema[];
};
export type LiveTestRequestSchema = {
  firm_id: string | string;
  internal_task_filter: InternalTaskFilter;
  external_task_filter: ExternalTaskFilter;
  /** Type of rule to test. LINKING shows what links would be created, EXCLUSION shows what external tasks would be excluded. */
  rule_type?: ExternalTaskRuleType;
  /** Maximum matches to return. */
  limit?: number;
};
export type ExternalTaskPreviewSchema = {
  id: string | string;
  name: string;
  status?: ExternalTaskStatus;
  assignee_name?: string | null;
  due_date?: string | null;
  matter_id: string | string;
  matter_name?: string | null;
};
export type PreviewExternalFilterResponseSchema = {
  total_count: number;
  tasks: ExternalTaskPreviewSchema[];
};
export type PreviewExternalFilterRequestSchema = {
  firm_id: string | string;
  external_task_filter: ExternalTaskFilter;
  /** Maximum tasks to return. */
  limit?: number;
};
export type InternalTaskPreviewSchema = {
  id: string | string;
  name: string;
  task_group_id?: (string | string) | null;
  task_type_id?: (string | string) | null;
  task_type_name?: string | null;
  matter_id: string | string;
  matter_name?: string | null;
  firm_task_template_id?: (string | string) | null;
};
export type PreviewInternalFilterResponseSchema = {
  total_count: number;
  tasks: InternalTaskPreviewSchema[];
};
export type PreviewInternalFilterRequestSchema = {
  firm_id: string | string;
  internal_task_filter: InternalTaskFilter;
  /** Maximum tasks to return. */
  limit?: number;
};
export type FirmFilevineOrganizationLinkSchema = {
  firm_id: string | string;
  firm_name: string;
  filevine_organization_id: (string | string) | null;
  filevine_org_name: string | null;
  /** The Filevine organization ID */
  filevine_id?: number | null;
};
export type LinkFirmToOrganizationSchema = {
  /** The UUID of the FilevineOrganization to link to */
  filevine_organization_id: string | string;
};
export type FilevineOrganizationWithLinkStatusSchema = {
  id: string | string;
  filevine_id: number;
  org_name: string;
  created_at: string;
  modified_at: string;
  /** ID of the firm linked to this organization, if any */
  linked_firm_id?: (string | string) | null;
  /** Name of the firm linked to this organization, if any */
  linked_firm_name?: string | null;
};
export type CanonicalSectionType = "medical_providers" | "coverage";
export type FilevineSectionConfigDetailedSchema = {
  id: string | string;
  project_type_id: string | string;
  filevine_id: number;
  name: string;
  internal_name: string;
  section_type?: CanonicalSectionType;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineProjectConfigWithSectionsSchema = {
  id: string | string;
  organization_id: string | string;
  filevine_id: number;
  name: string;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
  sections: FilevineSectionConfigDetailedSchema[];
};
export type ProjectConfigSyncResultSchema = {
  project_configs_upserted?: number;
  section_configs_upserted?: number;
  field_configs_upserted?: number;
  organization_id?: (string | string) | null;
  workflow_id: string;
};
export type TriggerProjectConfigSyncSchema = {
  matter_id: string | string;
};
export type UpdateSectionTypeSchema = {
  /** The canonical section type to set (e.g., 'medical_providers') */
  section_type?: CanonicalSectionType;
};
export type FilevineContactPhoneSchema = {
  id: string | string;
  filevine_id: number;
  contact_id: string | string;
  phone_number: string;
  label: string | null;
  notes: string | null;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineContactEmailSchema = {
  id: string | string;
  filevine_id: number;
  contact_id: string | string;
  email: string;
  label: string | null;
  notes: string | null;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineContactAddressSchema = {
  id: string | string;
  filevine_id: number;
  contact_id: string | string;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  label: string | null;
  notes: string | null;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineContactWithHandlesSchema = {
  id: string | string;
  filevine_id: number;
  filevine_organization_id: string | string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  full_name: string;
  birth_date: string | null;
  is_archived: boolean;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
  phones?: FilevineContactPhoneSchema[];
  emails?: FilevineContactEmailSchema[];
  addresses?: FilevineContactAddressSchema[];
};
export type FilevineContactSchema = {
  id: string | string;
  filevine_id: number;
  filevine_organization_id: string | string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  full_name: string;
  birth_date: string | null;
  is_archived: boolean;
  data: {
    [key: string]: any;
  };
  created_at: string;
  modified_at: string;
};
export type FilevineContactSearchFieldType =
  | "full_name"
  | "first_name"
  | "last_name"
  | "phone"
  | "email"
  | "address";
export type FilevineContactMatchMetadata = {
  matched_field: FilevineContactSearchFieldType;
  matched_value: string;
  phone_handle_id?: (string | string) | null;
  email_handle_id?: (string | string) | null;
  address_handle_id?: (string | string) | null;
};
export type FilevineContactSearchResult = {
  contact: FilevineContactSchema;
  match: FilevineContactMatchMetadata;
  /** Trigram similarity score (1.0 = exact match) */
  similarity_score: number;
};
export type FilevineContactSearchResponse = {
  results: FilevineContactSearchResult[];
  query: string;
  organization_id: string | string;
  total_matches: number;
};
export type PrintableFilevineSectionSchema = {
  id: string | string;
  firm_id: string | string;
  section_name: string;
  section_url_component: string;
  created_at: string;
  modified_at: string;
};
export type CreatePrintableFilevineSectionSchema = {
  firm_id: string | string;
  section_name: string;
  section_url_component: string;
};
export type UpdatePrintableFilevineSectionSchema = {
  section_name?: string | null;
  section_url_component?: string | null;
};
export type UpdatePrintableFilevineSectionSchema2 = {
  section_name?: string | null;
  section_url_component?: string | null;
};
export type ServiceSchema = {
  id: string | string;
  name: string;
  display_name: string;
  description: string;
};
export type PoliceReportServiceConfig = {
  service_name?: "police_reports";
};
export type IntakeServiceConfig = {
  service_name?: "intake";
};
export type CaseSetupServiceConfig = {
  service_name?: "case_setup";
};
export type ClientCommunicationServiceConfig = {
  service_name?: "client_communication";
};
export type ClaimsServiceConfig = {
  service_name?: "claims";
};
export type MedicalRecordsServiceConfig = {
  service_name?: "medical_records_and_treatment";
};
export type DemandsServiceConfig = {
  service_name?: "demands";
};
export type LiensServiceConfig = {
  service_name?: "liens";
};
export type FirmServiceWithWorkflowsSchema = {
  id: string | string;
  firm_id: string | string;
  service_id: string | string;
  service: ServiceSchema;
  notes: string;
  config?:
    | (
        | PoliceReportServiceConfig
        | IntakeServiceConfig
        | CaseSetupServiceConfig
        | ClientCommunicationServiceConfig
        | ClaimsServiceConfig
        | MedicalRecordsServiceConfig
        | DemandsServiceConfig
        | LiensServiceConfig
      )
    | null;
  is_active?: boolean;
  workflows: {
    [key: string]: any;
  }[];
};
export type FirmServiceSchema = {
  id: string | string;
  firm_id: string | string;
  service_id: string | string;
  service: ServiceSchema;
  notes: string;
  config?:
    | (
        | PoliceReportServiceConfig
        | IntakeServiceConfig
        | CaseSetupServiceConfig
        | ClientCommunicationServiceConfig
        | ClaimsServiceConfig
        | MedicalRecordsServiceConfig
        | DemandsServiceConfig
        | LiensServiceConfig
      )
    | null;
  is_active?: boolean;
};
export type CreateFirmServiceSchema = {
  service_id: string | string;
};
export type WorkflowCode =
  | "coverage_1p_enabled"
  | "coverage_3p_enabled"
  | "medpay_enabled"
  | "police_report_request"
  | "incident_report_request"
  | "foia_request"
  | "social_media_check"
  | "dmv_accident_report_submission"
  | "dmv_record_request"
  | "medical_records"
  | "treatment"
  | "client_check_in"
  | "draft_demand"
  | "welcome_call"
  | "retainer_agreement"
  | "hipaa_directive"
  | "conduct_initial_review"
  | "client_drivers_license"
  | "defendant_drivers_license"
  | "client_health_insurance"
  | "client_auto_insurance"
  | "request_supplemental_details"
  | "automated_introduction"
  | "lien_management"
  | "email_settings"
  | "mini_tort_letter";
export type WorkflowStatus = "active" | "coming_soon";
export type CheckInMethod = "phone" | "text" | "email";
export type ClientCheckInWorkflowConfig = {
  check_in_methods?: CheckInMethod[];
};
export type SlaPolicy =
  | "4_BUSINESS_HOURS"
  | "6_BUSINESS_HOURS"
  | "8_BUSINESS_HOURS"
  | "0_DAYS"
  | "1_DAY"
  | "2_DAYS"
  | "3_DAYS"
  | "7_DAYS"
  | "10_DAYS"
  | "12_DAYS"
  | "14_DAYS"
  | "30_DAYS"
  | "45_DAYS"
  | "1_MONTH"
  | "2_MONTHS"
  | "3_MONTHS"
  | "4_MONTHS"
  | "5_MONTHS"
  | "6_MONTHS"
  | "2_WEEKS";
export type ConductInitialReviewWorkflowConfig = {
  sla_policy?: SlaPolicy;
};
export type BenefitOpeningPolicy =
  | "ALWAYS"
  | "VERIFY_WITH_CLIENT"
  | "AS_NEEDED"
  | "NEVER";
export type Coverage1PWorkflowConfig = {
  open_1p_coverage?: BenefitOpeningPolicy;
  open_medpay?: BenefitOpeningPolicy;
  open_pip?: BenefitOpeningPolicy;
  open_um?: BenefitOpeningPolicy;
  open_uim?: BenefitOpeningPolicy;
};
export type Coverage3PWorkflowConfig = {};
export type EmailSettingsWorkflowConfig = {
  auto_cc_emails?: string[];
};
export type OpenFileTaskInsuranceType =
  | "MEDICARE"
  | "MEDICAID"
  | "PRIVATE_INSURANCE";
export type LienNoticeDocument = "HIPAA_AUTH" | "HEALTH_INSURANCE_CARD" | "DOR";
export type OpenFileTaskRecipient =
  | "HEALTH_INSURANCE_ONLY"
  | "HEALTH_INSURANCE_AND_SUBROGATION";
export type LienManagementWorkflowConfig = {
  open_file_insurance_types?: OpenFileTaskInsuranceType[];
  lien_notice_documents?: LienNoticeDocument[];
  other_documents_to_include?: string;
  open_file_recipient?: OpenFileTaskRecipient;
  firm_state_instructions?: string;
};
export type TreatmentCheckInCadenceDays = "7" | "14" | "21" | "30";
export type TreatmentWorkflowConfig = {
  check_in_cadence_days?: TreatmentCheckInCadenceDays;
};
export type WelcomeCallWorkflowConfig = {
  sla_policy?: SlaPolicy;
};
export type RetainerAgreementWorkflowConfig = {
  sla_policy?: SlaPolicy;
};
export type HipaaDirectiveWorkflowConfig = {
  sla_policy?: SlaPolicy;
};
export type ClientDriversLicenseWorkflowConfig = {
  sla_policy?: SlaPolicy;
};
export type DefendantDriversLicenseWorkflowConfig = {
  sla_policy?: SlaPolicy;
};
export type ClientHealthInsuranceWorkflowConfig = {
  sla_policy?: SlaPolicy;
};
export type ClientAutoInsuranceWorkflowConfig = {
  sla_policy?: SlaPolicy;
};
export type RequestSupplementalDetailsWorkflowConfig = {
  sla_policy?: SlaPolicy;
};
export type AutomatedIntroductionWorkflowConfig = {
  send_email?: boolean;
  send_sms?: boolean;
};
export type ConfigFieldType =
  | "single_select"
  | "multi_select"
  | "text"
  | "number"
  | "text_list";
export type ConfigFieldOption = {
  value: string;
  label: string;
};
export type WorkflowConfigFieldSchema = {
  key: string;
  field_type: ConfigFieldType;
  label: string;
  description?: string | null;
  options?: ConfigFieldOption[] | null;
  status?: WorkflowStatus;
};
export type TaskType =
  | "Medical Records"
  | "Medical Bills"
  | "LOR Acknowledgement"
  | "Send LOR"
  | "Open Claim"
  | "Final Lien"
  | "Lien Acknowledgement"
  | "Police Report"
  | "Police Report Escalation"
  | "Freedom of Information Act"
  | "Incident Report"
  | "Open File"
  | "Find Health Insurance Coverage"
  | "Locate Report Source"
  | "Ascertain Subrogator"
  | "Finalize Demand"
  | "Draft Demand"
  | "Demand Readiness Review"
  | "Declaration Page"
  | "Health Insurance Card"
  | "Treatment Check-In"
  | "Treatment Barrier"
  | "HIPAA Directive"
  | "Retainer Agreement"
  | "Conduct Intake"
  | "Conduct Initial Review"
  | "Welcome Call"
  | "Other"
  | "All"
  | "Review Found Coverage"
  | "Review Found Provider"
  | "Update External CMS"
  | "Find 1P Coverage"
  | "Find 3P Coverage"
  | "Client Communications"
  | "Managing Paralegal Asks"
  | "Manage MedPay"
  | "Send MedPay Letter"
  | "Get Liability Limits"
  | "Request Updated PIP Ledger"
  | "Send Evidence Preservation Letter"
  | "Document Collection"
  | "Review 1P Benefits"
  | "PIP/MedPay Application"
  | "Check for PIP Ledger"
  | "IME Required"
  | "UM/UIM Indicate Stack vs. No Stack"
  | "Generate No Medpay Letter"
  | "Provide PIP Info to Medical Provider"
  | "Liability Other"
  | "1P Insurance Other"
  | "3P Insurance Other"
  | "Liens Other"
  | "Treatment Other"
  | "Medical Records Other"
  | "Demand Letter Other"
  | "Confirm Incident Location"
  | "Research Property & Owners"
  | "Mini Tort Review"
  | "Mini Tort Review Escalation"
  | "Mini Tort Letter"
  | "Mini Tort Chase";
export type FirmServiceWorkflowSchema = {
  id: string | string;
  firm_service_id: string | string;
  workflow_code: WorkflowCode;
  display_name: string;
  description: string | null;
  status: WorkflowStatus;
  is_enabled: boolean;
  notes: string;
  config?:
    | (
        | ClientCheckInWorkflowConfig
        | ConductInitialReviewWorkflowConfig
        | Coverage1PWorkflowConfig
        | Coverage3PWorkflowConfig
        | EmailSettingsWorkflowConfig
        | LienManagementWorkflowConfig
        | TreatmentWorkflowConfig
        | WelcomeCallWorkflowConfig
        | RetainerAgreementWorkflowConfig
        | HipaaDirectiveWorkflowConfig
        | ClientDriversLicenseWorkflowConfig
        | DefendantDriversLicenseWorkflowConfig
        | ClientHealthInsuranceWorkflowConfig
        | ClientAutoInsuranceWorkflowConfig
        | RequestSupplementalDetailsWorkflowConfig
        | AutomatedIntroductionWorkflowConfig
      )
    | null;
  config_fields?: WorkflowConfigFieldSchema[];
  task_types?: TaskType[];
};
export type WorkflowConfigSchema = {
  workflow_code: WorkflowCode;
  is_enabled: boolean;
  notes?: string;
  config?:
    | (
        | ClientCheckInWorkflowConfig
        | ConductInitialReviewWorkflowConfig
        | Coverage1PWorkflowConfig
        | Coverage3PWorkflowConfig
        | EmailSettingsWorkflowConfig
        | LienManagementWorkflowConfig
        | TreatmentWorkflowConfig
        | WelcomeCallWorkflowConfig
        | RetainerAgreementWorkflowConfig
        | HipaaDirectiveWorkflowConfig
        | ClientDriversLicenseWorkflowConfig
        | DefendantDriversLicenseWorkflowConfig
        | ClientHealthInsuranceWorkflowConfig
        | ClientAutoInsuranceWorkflowConfig
        | RequestSupplementalDetailsWorkflowConfig
        | AutomatedIntroductionWorkflowConfig
      )
    | null;
};
export type UpdateFirmServiceWorkflowsRequest = {
  workflows: WorkflowConfigSchema[];
};
export type UpdateFirmServiceSchema = {
  is_active?: boolean | null;
  notes?: string | null;
  config?:
    | (
        | PoliceReportServiceConfig
        | IntakeServiceConfig
        | CaseSetupServiceConfig
        | ClientCommunicationServiceConfig
        | ClaimsServiceConfig
        | MedicalRecordsServiceConfig
        | DemandsServiceConfig
        | LiensServiceConfig
      )
    | null;
};
export type FirmServicesActivationResponse = {
  firm_id: string | string;
  activated: boolean;
  already_active?: boolean;
};
export type FirmServicesStatusResponse = {
  firm_id: string | string;
  is_activated: boolean;
  matters_count?: number;
};
export type BulkSyncExecuteResponse = {
  workflow_id: string;
  run_id: string;
  firm_id: string | string;
  firm_name: string;
  matters_count: number;
  status?: string;
};
export type BulkSyncPreviewResponse = {
  firm_id: string | string;
  firm_name: string;
  matters_count: number;
  dry_run?: boolean;
  exclude_statuses: string[];
};
export type SmsTemplateCategory =
  | "Document Collection"
  | "Initial Treatment Assessment"
  | "Ongoing Treatment Check-In";
export type SmsTemplateSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  /** The firm this template belongs to. Null for global templates. */
  firm_id?: (string | string) | null;
  /** Human-readable name for the template */
  name: string;
  /** The communication stage this template is for */
  category: SmsTemplateCategory;
  /** Template content with {{merge_fields}} placeholders */
  content: string;
  /** Whether this template is available for use */
  is_active: boolean;
  /** True for system-wide templates, false for firm-specific */
  is_global: boolean;
  /** Default template for its category (within firm or global scope). */
  is_default?: boolean;
  /** Lower numbers are preferred when selecting a recommended template. */
  priority?: number;
  /** Days since last client response this template targets. */
  cadence_day?: number | null;
  /** User who created this template. Null for seeded global templates. */
  created_by_id?: (string | string) | null;
};
export type SmsTemplateListResponse = {
  /** List of SMS templates */
  templates: SmsTemplateSchema[];
  /** Total number of templates matching the query */
  total_count: number;
};
export type CreateSmsTemplateRequest = {
  /** Human-readable name for the template */
  name: string;
  /** The communication stage this template is for */
  category: SmsTemplateCategory;
  /** Template content with {{merge_fields}} placeholders */
  content: string;
  /** Whether this template is available for use */
  is_active?: boolean;
  /** Default template for its category (within firm or global scope). */
  is_default?: boolean;
  /** Lower numbers are preferred when selecting a recommended template. */
  priority?: number;
  /** Days since last client response this template targets. */
  cadence_day?: number | null;
};
export type UpdateSmsTemplateRequest = {
  /** Human-readable name for the template */
  name?: string | null;
  /** Template content with {{merge_fields}} placeholders */
  content?: string | null;
  /** Whether this template is available for use */
  is_active?: boolean | null;
  /** Default template for its category (within firm or global scope). */
  is_default?: boolean | null;
  /** Lower numbers are preferred when selecting a recommended template. */
  priority?: number | null;
  /** Days since last client response this template targets. */
  cadence_day?: number | null;
};
export type FirmTaskTemplateResponse = {
  id: string;
  firm_id: string;
  /** * `auto_create` - auto_create
   * `manual_only` - manual_only
   * `archived` - archived */
  status: "auto_create" | "manual_only" | "archived";
  task_type: string;
  task_group: string;
  name: string;
  description: string;
  assignee_role: string | null;
  sla_resolution_policy: string | null;
  created_by_id: string | null;
  created_by_name: string | null;
  created_at: string;
  modified_at: string;
};
export type AssigneeRole =
  | "managing_paralegal"
  | "case_manager"
  | "police_report_manager"
  | "foia_manager"
  | "pip_medpay_manager"
  | "medical_records_manager"
  | "demands_team"
  | "benefits_specialist"
  | "coverage_team"
  | "claims_team"
  | "acknowledgement_team"
  | "treatment_team"
  | "draft_demand_team"
  | "finalize_demand_team"
  | "demand_readiness_team"
  | "liens_team";
export type FirmTaskTemplateStatus = "auto_create" | "manual_only" | "archived";
export type CreateFirmTaskTemplateSchema = {
  name: string;
  description?: string;
  task_type: TaskType;
  assignee_role: AssigneeRole;
  sla_resolution_policy: SlaPolicy;
  status?: FirmTaskTemplateStatus;
};
export type UpdateFirmTaskTemplateSchema = {
  name?: string | null;
  description?: string | null;
  task_type?: TaskType;
  assignee_role?: AssigneeRole;
  sla_resolution_policy?: SlaPolicy;
  status?: FirmTaskTemplateStatus;
};
export type ApplyResponse = {
  workflow_id: string;
  status: string;
  total_matters: number;
};
export type ApplyPreviewResponse = {
  total_matters: number;
  will_create: number;
  already_exists: number;
};
export type OptionsResponse = {
  assignee_roles: {
    [key: string]: any;
  }[];
  sla_policies: {
    [key: string]: any;
  }[];
  task_types: {
    [key: string]: any;
  }[];
  statuses: {
    [key: string]: any;
  }[];
};
export type DocumentTemplate = {
  name: string;
  display_name?: string | null;
};
export type DocumentTemplateRead = {
  id: string;
  name: string;
  display_name?: string | null;
  category: string;
  format: string;
  scope: string;
  status: string;
  attorney_id: string | null;
  attorney_name: string | null;
  requires_signatures: boolean;
};
export type DialpadPhoneNumberSourceType = "office" | "department";
export type DialpadPhoneNumberOption = {
  /** Phone number in E.164 format */
  phone_number: string;
  /** Human-readable display name for the dropdown */
  display_name: string;
  /** Whether this is an office or department number */
  source_type: DialpadPhoneNumberSourceType;
  /** The ID of the office or department */
  source_id: number;
  /** The name of the office or department */
  source_name?: string | null;
};
export type DialpadPhoneNumberListResponse = {
  /** Available phone numbers for selection */
  numbers?: DialpadPhoneNumberOption[];
};
export type UserSimpleSchema = {
  id: string | string;
  display_name: string;
  email: string;
};
export type FirmRoleAssignmentConfigSchema = {
  /** Whether this case manager is eligible for auto-assignment to new matters */
  case_auto_assign?: boolean;
};
export type FirmRoleAssignmentDetailSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  /** The firm for which this role assignment applies */
  firm_id: string | string;
  /** The role being assigned (e.g., medical_records_manager, claims_manager) */
  role: AssigneeRole;
  /** The user assigned to this role for this firm */
  assignee_id: string | string;
  /** Full details of the assigned user */
  assignee: UserSimpleSchema;
  /** Role-specific configuration */
  config?: FirmRoleAssignmentConfigSchema;
};
export type CreateFirmRoleAssignmentSchema = {
  firm_id: string | string;
  role: AssigneeRole;
  assignee_id: string | string;
  /** Role-specific configuration (e.g., case_auto_assign for case managers) */
  config?: FirmRoleAssignmentConfigSchema;
};
export type UpdateFirmRoleAssignmentSchema = {
  assignee_id?: (string | string) | null;
  config?: FirmRoleAssignmentConfigSchema;
};
export type FirmDetailLite = {
  name: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  managing_paralegal_id: string | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Structured intake preferences organized by sections */
  intake_preferences?: any;
  /** Rich text field for case preferences and guidelines */
  case_preferences?: string | null;
  /** Rich text field for Head of Operations commentary and updates */
  commentary?: string | null;
  /** Rich text field for tracking errors and issues */
  errors?: string | null;
  /** Rich text field for derisking plans and strategies */
  plan_to_derisk?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
  /** Strategy for auto-assigning case managers to new matters
    
    * `disabled` - disabled
    * `round_robin` - round_robin */
  case_manager_assignment_strategy?: "disabled" | "round_robin";
  /** Unique subdomain for white-labeled portal (e.g., 'acmelaw' → acmelaw.portal.example.com) */
  client_portal_subdomain?: string | null;
  /** Primary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_primary_color?: string | null;
  /** Secondary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_secondary_color?: string | null;
};
export type FirmDetailLiteRead = {
  id: string;
  name: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  managing_paralegal: CustomUserRead | null;
  managing_paralegal_id: string | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  created_at: string;
  modified_at: string;
  hipaa_cover_sheet_template: string | null;
  hipaa_reproductive_rights_attestation_url: string | null;
  hipaa_attorney_relationship_form_url: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Structured intake preferences organized by sections */
  intake_preferences?: any;
  /** Rich text field for case preferences and guidelines */
  case_preferences?: string | null;
  /** Rich text field for Head of Operations commentary and updates */
  commentary?: string | null;
  /** Rich text field for tracking errors and issues */
  errors?: string | null;
  /** Rich text field for derisking plans and strategies */
  plan_to_derisk?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  front_channel: {
    [key: string]: any;
  } | null;
  address: AddressRead | null;
  /** Get all feature flag values for this firm. */
  feature_flags: {
    [key: string]: string;
  };
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
  /** Strategy for auto-assigning case managers to new matters
    
    * `disabled` - disabled
    * `round_robin` - round_robin */
  case_manager_assignment_strategy?: "disabled" | "round_robin";
  /** Unique subdomain for white-labeled portal (e.g., 'acmelaw' → acmelaw.portal.example.com) */
  client_portal_subdomain?: string | null;
  client_portal_logo_url: string | null;
  /** Primary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_primary_color?: string | null;
  /** Secondary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_secondary_color?: string | null;
  letterhead_url: string | null;
  firm_footer_url: string | null;
  firm_logo_url: string | null;
  firm_watermark_url: string | null;
};
export type PatchedFirm = {
  name?: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  managing_paralegal_id?: string | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Structured intake preferences organized by sections */
  intake_preferences?: any;
  /** Rich text field for case preferences and guidelines */
  case_preferences?: string | null;
  /** Rich text field for Head of Operations commentary and updates */
  commentary?: string | null;
  /** Rich text field for tracking errors and issues */
  errors?: string | null;
  /** Rich text field for derisking plans and strategies */
  plan_to_derisk?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
  /** Strategy for auto-assigning case managers to new matters
    
    * `disabled` - disabled
    * `round_robin` - round_robin */
  case_manager_assignment_strategy?: "disabled" | "round_robin";
  /** Unique subdomain for white-labeled portal (e.g., 'acmelaw' → acmelaw.portal.example.com) */
  client_portal_subdomain?: string | null;
  /** Primary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_primary_color?: string | null;
  /** Secondary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_secondary_color?: string | null;
};
export type PatchedFirmRead = {
  id?: string;
  name?: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  managing_paralegal?: CustomUserRead | null;
  managing_paralegal_id?: string | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  created_at?: string;
  modified_at?: string;
  hipaa_cover_sheet_template?: string | null;
  hipaa_reproductive_rights_attestation_url?: string | null;
  hipaa_attorney_relationship_form_url?: string | null;
  /** Get all HIPAA cover sheet templates for this firm via v2 Template model. */
  hipaa_cover_sheet_templates?: {
    [key: string]: any;
  }[];
  /** Get the ID of the default HIPAA template for this firm via v2 Template model. */
  default_hipaa_template_id?: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Structured intake preferences organized by sections */
  intake_preferences?: any;
  /** Rich text field for case preferences and guidelines */
  case_preferences?: string | null;
  /** Rich text field for Head of Operations commentary and updates */
  commentary?: string | null;
  /** Rich text field for tracking errors and issues */
  errors?: string | null;
  /** Rich text field for derisking plans and strategies */
  plan_to_derisk?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  front_channel?: {
    [key: string]: any;
  } | null;
  address?: AddressRead | null;
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
  /** Strategy for auto-assigning case managers to new matters
    
    * `disabled` - disabled
    * `round_robin` - round_robin */
  case_manager_assignment_strategy?: "disabled" | "round_robin";
  /** Unique subdomain for white-labeled portal (e.g., 'acmelaw' → acmelaw.portal.example.com) */
  client_portal_subdomain?: string | null;
  client_portal_logo_url?: string | null;
  /** Primary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_primary_color?: string | null;
  /** Secondary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_secondary_color?: string | null;
  letterhead_url?: string | null;
  firm_footer_url?: string | null;
  firm_logo_url?: string | null;
  firm_watermark_url?: string | null;
};
export type PatchedFirmWrite = {
  name?: string;
  full_name?: string | null;
  filevine_org_id?: number | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  /** * `Active` - Active
   * `Testing` - Testing
   * `Software Only` - Software Only
   * `New Lead` - New Lead
   * `Open` - Open
   * `Closed` - Closed */
  status?:
    | (
        | "Active"
        | "Testing"
        | "Software Only"
        | "New Lead"
        | "Open"
        | "Closed"
        | ""
        | null
      )
    | null;
  /** * `Solo` - Solo
   * `Small` - Small
   * `Medium` - Medium
   * `Enterprise` - Enterprise */
  type?: ("Solo" | "Small" | "Medium" | "Enterprise" | "" | null) | null;
  managing_paralegal_id?: string | null;
  is_paying?: boolean;
  /** Whether this firm is a mini-tort shadow firm */
  is_mini_tort?: boolean;
  paying_since?: string | null;
  /** * `PST` - PST
   * `MST` - MST
   * `CST` - CST
   * `EST` - EST
   * `AKST` - AKST
   * `AST` - AST
   * `HST` - HST */
  time_zone?:
    | ("PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST" | "" | null)
    | null;
  primary_state?: string | null;
  /** The case management system used by this firm
    
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks
    * `Lawmatics` - Lawmatics */
  cms_system?:
    | (
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | "Lawmatics"
        | ""
        | null
      )
    | null;
  /** Link to the firm's CMS system */
  cms_link?: string | null;
  /** The lead management system used by this firm
    
    * `LeadDocket` - LeadDocket
    * `LawProNation` - LawProNation
    * `ClioGrow` - ClioGrow
    * `Intaker` - Intaker
    * `Lawmatics` - Lawmatics
    * `CASEpeer` - CASEpeer
    * `Filevine` - Filevine
    * `Litify` - Litify
    * `MyCase` - MyCase
    * `Clio Manage` - Clio Manage
    * `SmartAdvocate` - SmartAdvocate
    * `PracticePanther` - PracticePanther
    * `Needles` - Needles
    * `Neos` - Neos
    * `Merus` - Merus
    * `TrialWorks` - TrialWorks */
  lead_system?:
    | (
        | "LeadDocket"
        | "LawProNation"
        | "ClioGrow"
        | "Intaker"
        | "Lawmatics"
        | "CASEpeer"
        | "Filevine"
        | "Litify"
        | "MyCase"
        | "Clio Manage"
        | "SmartAdvocate"
        | "PracticePanther"
        | "Needles"
        | "Neos"
        | "Merus"
        | "TrialWorks"
        | ""
        | null
      )
    | null;
  /** Link to the firm's lead system */
  lead_system_link?: string | null;
  /** Structured intake preferences organized by sections */
  intake_preferences?: any;
  /** Rich text field for case preferences and guidelines */
  case_preferences?: string | null;
  /** Rich text field for Head of Operations commentary and updates */
  commentary?: string | null;
  /** Rich text field for tracking errors and issues */
  errors?: string | null;
  /** Rich text field for derisking plans and strategies */
  plan_to_derisk?: string | null;
  /** Typed billing configuration (see app.defs.firms.FirmBillingConfig) */
  billing_config?: any;
  /** If true, after importing/syncing external tasks for a matter, automatically run a rule backfill for that matter to create any missing rule links. */
  auto_backfill_external_task_rules_on_import?: boolean;
  front_channel_front_id?: string | null;
  /** Department phone number for sending SMS in E.164 format (e.g., +15551234567) */
  sms_sender_number?: string | null;
  /** Display name for SMS sender (e.g., 'Finch Legal') */
  sms_sender_name?: string | null;
  /** Firm fax number */
  fax?: string | null;
  /** Employer Identification Number (EIN) in XX-XXXXXXX format */
  ein?: string | null;
  /** Strategy for auto-assigning case managers to new matters
    
    * `disabled` - disabled
    * `round_robin` - round_robin */
  case_manager_assignment_strategy?: "disabled" | "round_robin";
  /** Unique subdomain for white-labeled portal (e.g., 'acmelaw' → acmelaw.portal.example.com) */
  client_portal_subdomain?: string | null;
  /** Primary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_primary_color?: string | null;
  /** Secondary brand color for the client portal (hex format: #RRGGBB) */
  client_portal_secondary_color?: string | null;
};
export type AddressSchema = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province_or_state?: string | null;
  postal_or_zip?: string | null;
  country_code?: string | null;
};
export type CreateAddressSchema = {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
};
export type CreateAddressSchema2 = {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
};
export type SetClientPortalLogoSchema = {
  client_portal_logo?: Blob | null;
};
export type SetFirmFooterSchema = {
  firm_footer?: Blob | null;
};
export type SetFirmLogoSchema = {
  firm_logo?: Blob | null;
};
export type SetFirmWatermarkSchema = {
  firm_watermark?: Blob | null;
};
export type SetHipaaAttorneyRelationshipFormSchema = {
  hipaa_attorney_relationship_form?: Blob | null;
};
export type SetHipaaReproductiveRightsAttestationSchema = {
  hipaa_reproductive_rights_attestation?: Blob | null;
};
export type SetFirmLetterheadSchema = {
  letterhead?: Blob | null;
};
export type WorkflowCatalogueItemSchema = {
  code: string;
  display_name: string;
  description?: string | null;
  status?: string;
  config_fields?: {
    [key: string]: any;
  }[];
  task_types?: string[];
};
export type ServiceCatalogueItemSchema = {
  id: string | string;
  name: string;
  display_name: string;
  description: string;
  workflows?: WorkflowCatalogueItemSchema[];
};
export type ServiceCatalogueSchema = {
  services: ServiceCatalogueItemSchema[];
};
export const {
  useFirmsListQuery,
  useFirmsCreateMutation,
  useFirmsBlobSinkConfigsListQuery,
  useFirmsBlobSinkConfigsCreateMutation,
  useFirmsBlobSinkConfigsRetrieveQuery,
  useFirmsBlobSinkConfigsPartialUpdateMutation,
  useFirmsBlobSinkConfigsDestroyMutation,
  useFirmsBlobSinkConfigsPromptPreviewRunsQuery,
  useFirmsBlobSinkConfigsPromptPreviewRuns2Mutation,
  useFirmsBlobSinkConfigsPromptPreviewRunsRetrieveQuery,
  useFirmsBlobSinkConfigsPromptPreviewRunsApplyMutation,
  useFirmsBlobSinkConfigsPromptPreviewRunsLatestQuery,
  useFirmsBlobSinkConfigsGuessConventionQuery,
  useFirmCaseManagersListQuery,
  useFirmsCmsFeatureConfigsListQuery,
  useFirmsCmsFeatureConfigsCreateMutation,
  useFirmsCmsFeatureConfigsRetrieveQuery,
  useFirmsCmsFeatureConfigsPartialUpdateMutation,
  useFirmsCmsFeatureConfigsDestroyMutation,
  useFirmsCmsFeatureConfigsDetailsQuery,
  useFirmsCmsFeatureConfigsTriggerMutation,
  useFirmsCmsFeatureConfigsSupportedFeaturesQuery,
  useExternalTaskRuleListQuery,
  useExternalTaskRuleCreateMutation,
  useExternalTaskRuleRetrieveQuery,
  useExternalTaskRulePartialUpdateMutation,
  useExternalTaskRuleDestroyMutation,
  useExternalTaskRuleUpdateConfigMutation,
  useExternalTaskRuleVersionsQuery,
  useExternalTaskRuleStartBackfillMutation,
  useExternalTaskRuleGetBackfillProgressQuery,
  useExternalTaskRuleCancelBackfillMutation,
  useExternalTaskRuleListBackfillsQuery,
  useExternalTaskRuleDistinctNamesQuery,
  useExternalTaskRuleLiveTestMutation,
  useExternalTaskRulePreviewExternalFilterMutation,
  useExternalTaskRulePreviewInternalFilterMutation,
  useFilevineOrganizationLinkGetQuery,
  useFilevineOrganizationLinkCreateMutation,
  useFilevineOrganizationsListQuery,
  useFilevineOrganizationUnlinkMutation,
  useFilevineProjectConfigsListQuery,
  useFilevineProjectConfigSyncTriggerMutation,
  useFilevineSectionConfigsListQuery,
  useFilevineSectionConfigRetrieveQuery,
  useFilevineSectionConfigUpdateTypeMutation,
  useFilevineContactRetrieveQuery,
  useFilevineContactByFilevineIdQuery,
  useFilevineContactSearchQuery,
  usePrintableFilevineSectionsListQuery,
  usePrintableFilevineSectionsCreateMutation,
  usePrintableFilevineSectionsRetrieveQuery,
  usePrintableFilevineSectionsUpdateMutation,
  usePrintableFilevineSectionsPartialUpdateMutation,
  usePrintableFilevineSectionsDestroyMutation,
  useGetFirmServicesQuery,
  useCreateFirmServiceMutation,
  useBulkUpdateFirmServiceWorkflowsMutation,
  useUpdateFirmServiceMutation,
  useActivateFirmServicesMutation,
  useGetFirmAutoCcEmailsQuery,
  useGetFirmServicesStatusQuery,
  useExecuteFirmServicesSyncMutation,
  usePreviewFirmServicesSyncQuery,
  useFirmSmsTemplatesListQuery,
  useFirmSmsTemplatesCreateMutation,
  useFirmSmsTemplatesRetrieveQuery,
  useFirmSmsTemplatesPartialUpdateMutation,
  useFirmSmsTemplatesDestroyMutation,
  useFirmTaskTemplatesListQuery,
  useFirmTaskTemplatesCreateMutation,
  useFirmTaskTemplatesRetrieveQuery,
  useFirmTaskTemplatesPartialUpdateMutation,
  useFirmTaskTemplatesApplyMutation,
  useFirmTaskTemplatesApplyPreviewMutation,
  useFirmTaskTemplatesOptionsQuery,
  useDocumentTemplatesListQuery,
  useDocumentTemplatesCreateMutation,
  useDocumentTemplateDownloadQuery,
  useDocumentTemplateEditorConfigQuery,
  useFirmDialpadNumbersListQuery,
  useListFirmRoleAssignmentsQuery,
  useCreateFirmRoleAssignmentMutation,
  useGetFirmRoleAssignmentQuery,
  useUpdateFirmRoleAssignmentMutation,
  useDeleteFirmRoleAssignmentMutation,
  useFirmsRetrieveQuery,
  useFirmsUpdateMutation,
  useFirmsPartialUpdateMutation,
  useGetFirmAddressQuery,
  useSetFirmAddress2Mutation,
  useSetFirmAddressMutation,
  useSetClientPortalLogoMutation,
  useSetFirmFooterMutation,
  useSetFirmLogoMutation,
  useSetFirmWatermarkMutation,
  useSetHipaaAttorneyRelationshipFormMutation,
  useSetHipaaReproductiveRightsAttestationMutation,
  useSetLetterheadMutation,
  useGetServiceCatalogueQuery,
} = injectedRtkApi;
